import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FlipOutlinedIcon from '@mui/icons-material/FlipOutlined';
import GridOnIcon from '@mui/icons-material/GridOn';
import PanToolOutlinedIcon from '@mui/icons-material/PanToolOutlined';
import RedoIcon from '@mui/icons-material/Redo';
import RefreshIcon from '@mui/icons-material/Refresh';
import StraightenIcon from '@mui/icons-material/Straighten';
import TextureIcon from '@mui/icons-material/Texture';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import UndoIcon from '@mui/icons-material/Undo';
import ColorizeOutlinedIcon from '@mui/icons-material/ColorizeOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import CropOutlinedIcon from '@mui/icons-material/CropOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import TransformOutlinedIcon from '@mui/icons-material/TransformOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import FileOpenOutlinedIcon from '@mui/icons-material/FileOpenOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  GRID_SIZE_OPTIONS,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  buildSaveFilePreview,
  clampZoom,
  computeFitZoom,
  snapAxisValue,
  snapCanvasPoint,
} from '../../lib/imageEditorGuides';
import {
  clampFloatScale,
  createFloat,
  cloneImageData as cloneFloatPixels,
  extractOpaqueContentFloat,
  fitFloatScaleToDocument,
  floatFromEdgeDrag,
  hitTestFloatHandle,
  hitTestRasterLayerPixel,
  imageDataToCanvas,
  normalizeRotationDeg,
  rotationDegFromPointer,
  scaleFromCornerDrag,
  shouldCommitFloatOnPointer,
  snapFloatTranslation,
  rasterizeFloatToDocumentPixels,
  withUniformFloatScale,
} from '../../lib/imageEditorFloat';
import {
  aspectRatioLabelFromSize,
  loadImageSrcToImageData,
  mapImageEditorAiError,
} from '../../lib/imageEditorAi';
import {
  selectionMaskToStabilityPngDataUrl,
} from '../../lib/imageEditorInpaintMask';
import {
  IMAGE_EDITOR_FORMATS,
  canvasHasTransparency,
  exportCanvasBlob,
  getImageEditorFormatMeta,
  normalizeImageEditorFormat,
} from '../../lib/imageEditorFormats';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Popover from '@mui/material/Popover';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import BrightnessHighIcon from '@mui/icons-material/BrightnessHigh';
import BrightnessLowIcon from '@mui/icons-material/BrightnessLow';
import DetailsIcon from '@mui/icons-material/Details';
import GradientIcon from '@mui/icons-material/Gradient';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import GestureIcon from '@mui/icons-material/Gesture';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import ImageEditorFloatOverlay from './ImageEditorFloatOverlay';
import ImageEditorSelectionOverlay from './ImageEditorSelectionOverlay';
import ImageEditorNewCanvasDialog, {
  IMAGE_EDITOR_MAX_CANVAS_DIMENSION,
  clampImageEditorCanvasDimension,
} from './ImageEditorNewCanvasDialog';
import {
  clearSelectionPixels,
  canvasAnchorOffset,
  cropImageData,
  ellipseSelectionMask,
  ensureSelectionSize,
  extractSelectionImageData,
  floodFillImageData,
  lassoSelectionMask,
  magicWandSelection,
  paintSelectionBrushDab,
  rectSelectionMask,
  invertSelectionMask,
  resizeCanvasImageData,
  scaleImageData,
  selectAllMask,
  selectionHasPixels,
  snapPointToEdge,
  sobelMagnitude,
} from '../../lib/imageEditorSelection';
import {
  applyBlurBrushDab,
  applyCloneStampDab,
  applyDodgeBurnBrushDab,
  applyLinearGradient,
  applySharpenBrushDab,
  applySmudgeBrushDab,
  applySpongeBrushDab,
  isRetouchBrushTool,
} from '../../lib/imageEditorRetouch';
import {
  BLEND_MULTIPLY,
  BLEND_NORMAL,
  BLEND_OVERLAY,
  BLEND_SCREEN,
  EDITOR_BLEND_MODES,
  addEditorLayer,
  addEditorTextLayer,
  applyEditorDocumentGeometry,
  clearCowUniqueLayerIds,
  compositeEditorDocument,
  createEditorDocumentFromImageData,
  createEditorDocumentHistory,
  deleteEditorLayer,
  duplicateEditorLayer,
  ensureLayerPixelsUnique,
  getActiveEditorLayer,
  layerNameFromFloatOrigin,
  patchEditorLayer,
  patchEditorTextLayer,
  presentEditorDocument,
  pushEditorDocumentHistory,
  rasterizeEditorTextLayer,
  redoEditorDocumentHistory,
  reorderEditorLayer,
  replaceEditorDocumentFlat,
  resolveLayerRenameCommit,
  restoreEditorDocumentFromSnapshot,
  sampleImageDataPixel,
  setActiveEditorLayer,
  setEditorLayerPixels,
  snapshotEditorDocument,
  undoEditorDocumentHistory,
} from '../../lib/imageEditorDocument';
import {
  cloneEditorTextLayerContent,
  editorTextLayerContentFromFloat,
  editorTextLayerToFloat,
  hitTestEditorTextLayer,
  isEditorTextLayer,
} from '../../lib/imageEditorTextLayer';
import {
  IMAGE_EDITOR_TEXT_FONTS,
  applyTextFloatContentPatch,
  flattenRunsToText,
  renderTextToImageData,
  renderTextToImageDataWithLayout,
  textLayerPreviewLabel,
} from '../../lib/imageEditorText';
import { isTextFloatEditSurfaceTarget } from '../../lib/imageEditorTextEdit';
import {
  PATH_CLOSE_SNAP_PX,
  appendPathAnchor,
  closeEditorPath,
  createEmptyEditorPath,
  pathNearFirstAnchor,
  pathToSelectionMask,
  rasterizePathToImageData,
  updateLastAnchorDragHandles,
} from '../../lib/imageEditorPath';
import {
  IDLE_BUSY_HEARTBEAT_MS,
  registerIdleUnsavedWorkChecker,
  reportAppActivity,
} from '../../lib/idleSession';
import {
  IMAGE_EDITOR_PROJECT_KIND,
} from '../../lib/imageEditorProjectFormat';
import {
  loadLayeredEditorProject,
} from '../../lib/imageEditorProjectFiles';

/** Max canvas dimension (px) to keep pixel processing fast. */
const MAX_CANVAS_DIMENSION = IMAGE_EDITOR_MAX_CANVAS_DIMENSION;
/** Ruler strip thickness (CSS px). */
const RULER_SIZE_PX = 22;
/** Invisible guide hit padding (CSS px) around the 2px visible line. */
const GUIDE_HIT_PAD_PX = 6;

const TOOL_REMOVE_BG = 'remove-bg';
const TOOL_PAINT = 'paint';
const TOOL_SELECT = 'select';
const TOOL_INPAINT = 'inpaint-tool';
const TOOL_MOVE = 'move';
const TOOL_FILL = 'fill';
const TOOL_CROP = 'crop';
const TOOL_SAMPLER = 'sampler';
const TOOL_CLONE = 'clone-stamp';
const TOOL_BLUR = 'blur';
const TOOL_SHARPEN = 'sharpen';
const TOOL_SMUDGE = 'smudge';
const TOOL_DODGE = 'dodge';
const TOOL_BURN = 'burn';
const TOOL_SPONGE = 'sponge';
const TOOL_GRADIENT = 'gradient';
const TOOL_TEXT = 'text';
const TOOL_PEN = 'pen';
const SELECT_RECT = 'rect';
const SELECT_ELLIPSE = 'ellipse';
const SELECT_LASSO = 'lasso';
const SELECT_POLY = 'poly';
const SELECT_MAGNETIC = 'magnetic';
const SELECT_WAND = 'wand';
const SELECT_BRUSH = 'brush';
const SELECT_MODE_OPTIONS = [
  { value: SELECT_RECT, label: 'Rectangle' },
  { value: SELECT_ELLIPSE, label: 'Ellipse' },
  { value: SELECT_LASSO, label: 'Freehand Lasso' },
  { value: SELECT_POLY, label: 'Polygonal Lasso' },
  { value: SELECT_MAGNETIC, label: 'Magnetic Lasso' },
  { value: SELECT_WAND, label: 'Magic Wand' },
  { value: SELECT_BRUSH, label: 'Brush' },
];
const MAX_COLOR_SAMPLES = 4;
const DEFAULT_MODAL_SIZE = { w: 1040, h: 780 };
/** Right options rail width (px) — resizable; persisted in sessionStorage. */
const OPTIONS_PANEL_WIDTH_DEFAULT = 200;
const OPTIONS_PANEL_WIDTH_MIN = 140;
const OPTIONS_PANEL_WIDTH_MAX = 420;
const OPTIONS_PANEL_WIDTH_STORAGE_KEY = 'shiftyIeOptionsPanelWidth';
const DEFAULT_MAGNETIC_RADIUS = 10;
const MIN_MAGNETIC_STEP_PX = 3;
/** Canvas-pixel radius: click near first poly vertex closes the polygon. */
const POLY_CLOSE_SNAP_PX = PATH_CLOSE_SNAP_PX;
const MIN_MODAL_SIZE = { w: 520, h: 420 };
const DEFAULT_RETOUCH_STRENGTH = 0.4;

const PIXEL_READ_ERROR =
  "Couldn't read image pixel data — this usually means the image host isn't sending CORS headers. Editing tools can't run until the image can be read safely.";

const isSelectionBuildingTool = (tool) =>
  tool === TOOL_SELECT || tool === TOOL_INPAINT;

const requestImageEditorAi = httpsCallable(functions, 'requestImageEditorAi', {
  timeout: 180000,
});

const GENERATE_MODE_TXT2IMG = 'txt2img';
const GENERATE_MODE_IMG2IMG = 'img2img';
const GENERATE_MODE_INPAINT = 'inpaint';
const DEFAULT_IMG2IMG_STRENGTH = 0.55;

/**
 * @param {{r:number,g:number,b:number}} a
 * @param {{r:number,g:number,b:number}} b
 * @returns {number}
 */
function colorDistance(a, b) {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db_ = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db_ * db_);
}

/**
 * @param {ImageData} imageData
 * @returns {ImageData}
 */
function cloneImageData(imageData) {
  return new ImageData(
    new Uint8ClampedArray(imageData.data),
    imageData.width,
    imageData.height,
  );
}

/**
 * @param {string} hex
 * @returns {{r:number,g:number,b:number}}
 */
function hexToRgb(hex) {
  const raw = typeof hex === 'string' ? hex.trim() : '';
  const m = /^#?([0-9a-f]{6})$/i.exec(raw);
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * @param {{r:number,g:number,b:number}} rgb
 * @returns {string}
 */
function rgbToHex(rgb) {
  const to = (n) =>
    Math.max(0, Math.min(255, Math.round(Number(n) || 0)))
      .toString(16)
      .padStart(2, '0');
  return `#${to(rgb.r)}${to(rgb.g)}${to(rgb.b)}`;
}

/**
 * Session-based multi-tool image editor with layered project persistence.
 *
 * Architecture: layered EditorDocument (ordered stack, CoW history) with a
 * dirty-flag composited display canvas. Rulers/grid/guides are viewport
 * chrome only. A single transient float may sit above the stack until
 * Commit (→ new layer) or Discard. Explicit Save still auto-commits an active
 * float first; silent autosave does not — it persists last-committed doc only
 * (keyed off document `id` + `revision`).
 *
 * @param {{
 *   open: boolean,
 *   imageUrl?: string,
 *   layeredNodeId?: string|null,
 *   assetLabel?: string,
 *   busy?: boolean,
 *   onClose: () => void,
 *   onSave: (blob: Blob, meta?: { suffix: string, fileStem: string, format: string }) => void,
 *   onSaveProject?: (payload: { document: import('../../lib/imageEditorDocument').EditorDocument, fileStem: string, kind: string }) => Promise<unknown>|unknown,
 *   onAutosaveProject?: (payload: { document: import('../../lib/imageEditorDocument').EditorDocument, fileStem: string, nodeId?: string|null, kind: string }) => Promise<{ nodeId?: string|null }|null>|null,
 *   blankSize?: { width: number, height: number },
 *   companyId?: string | null,
 *   projectId?: string | null,
 *   preferredFormat?: string,
 *   onDocumentChange?: (doc: { kind: 'blank'|'local', imageUrl: string, assetLabel: string, sourceAsset: object, objectUrl?: string|null }) => void,
 * }} props
 */
export default function ImageEditorModal({
  open,
  imageUrl = '',
  layeredNodeId = null,
  assetLabel = '',
  busy = false,
  onClose,
  onSave,
  onSaveProject = null,
  onAutosaveProject = null,
  blankSize,
  companyId = null,
  projectId = null,
  preferredFormat = 'png',
  onDocumentChange,
}) {
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  /** After install: apply fit-to-viewport zoom once the shell has layout. */
  const fitZoomPendingRef = useRef(false);
  /** @type {React.MutableRefObject<import('../../lib/imageEditorDocument').EditorDocument|null>} */
  const docRef = useRef(null);
  /** Pristine document snapshot from load — Reset all. */
  const pristineSnapRef = useRef(null);
  /** CoW history of document snapshots (shared ImageData refs for unchanged layers). */
  const historyRef = useRef(createEditorDocumentHistory());
  /** Offscreen canvas for painting onto the active layer. */
  const layerPaintCanvasRef = useRef(null);
  const layerPaintSeededRef = useRef(false);
  const paintingRef = useRef(false);
  const strokeDirtyRef = useRef(false);
  const lastPaintPtRef = useRef(null);
  /** Snapshot of the buffer at stroke start (clone/blur/sharpen/smudge sample source). */
  const strokeSourceRef = useRef(null);
  /** Working ImageData mutated across a retouch stroke. */
  const strokeWorkingRef = useRef(null);
  /** Clone stamp offset = sourceOrigin - strokeStart. */
  const cloneOffsetRef = useRef(null);
  /** Gradient drag start. */
  const gradientDragRef = useRef(null);
  /** @type {React.MutableRefObject<{axis:'v'|'h', index:number}|null>} */
  const draggingGuideRef = useRef(null);
  /** @type {React.MutableRefObject<{x:number,y:number,scrollLeft:number,scrollTop:number}|null>} */
  const panDragRef = useRef(null);
  const spaceHeldRef = useRef(false);
  const zHeldRef = useRef(false);
  /** @type {React.MutableRefObject<{mode:string, startClientX:number, startClientY:number, startFloat:object}|null>} */
  const floatDragRef = useRef(null);
  const selectDragRef = useRef(null);
  /** True while painting a Select-brush stroke into the mask. */
  const selectBrushStrokeRef = useRef(false);
  /** Erase (subtract) for the active Select-brush stroke. */
  const selectBrushEraseRef = useRef(false);
  /** Sobel magnitude cache for magnetic lasso (per stroke). */
  const magneticMagRef = useRef(null);
  /** Polygonal lasso in progress (points live in lassoPointsRef + selectDraft). */
  const polyActiveRef = useRef(false);
  /** Pen path in progress (anchors live in pathRef + pathDraft). */
  const pathRef = useRef(createEmptyEditorPath());
  const penDragRef = useRef(false);
  const selectModeMenuAnchorRef = useRef(null);
  const cropDragRef = useRef(null);
  const lassoPointsRef = useRef([]);
  const modalResizeRef = useRef(null);
  /** Last canvas pointer for paste placement. */
  const lastCanvasPtRef = useRef(null);
  /** In-modal clipboard ImageData (not OS clipboard). */
  const clipboardRef = useRef(null);
  const openInputRef = useRef(null);
  const importInputRef = useRef(null);

  const [loadError, setLoadError] = useState('');
  const [toolError, setToolError] = useState('');
  const [imageReady, setImageReady] = useState(false);
  /** Explicit Save / Save As in flight (blocks concurrent saves). */
  const [saveInFlight, setSaveInFlight] = useState(false);
  /** @type {['idle'|'saving'|'error', Function]} */
  const [autosaveStatus, setAutosaveStatus] = useState(
    /** @type {'idle'|'saving'|'error'} */ ('idle'),
  );
  const [dirty, setDirty] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [layersEpoch, setLayersEpoch] = useState(0);
  const [panelLayersOpen, setPanelLayersOpen] = useState(true);
  /** Layer id currently being renamed inline. */
  const [renamingLayerId, setRenamingLayerId] = useState(null);
  const [renamingLayerValue, setRenamingLayerValue] = useState('');
  /** Keep latest rename draft for blur commit (avoids stale state). */
  const renamingLayerValueRef = useRef('');
  /** When true, blur must not commit (Esc cancel). */
  const renameCancelRef = useRef(false);
  /** Layer id being dragged for reorder. */
  const [draggingLayerId, setDraggingLayerId] = useState(null);
  /** Drop-target layer id (insert at that stack position). */
  const [layerDropTargetId, setLayerDropTargetId] = useState(null);
  const [tool, setTool] = useState(TOOL_MOVE);
  /** Buffer pixel size — drives overlay % positions. */
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  /** @type {[import('../../lib/imageEditorFloat').EditorFloat|null, Function]} */
  const [floatObj, setFloatObj] = useState(null);
  const floatObjRef = useRef(null);
  /** Real <input> for text-float Content — focus without select-all. */
  const textContentInputRef = useRef(/** @type {HTMLInputElement|null} */ (null));
  const textContentAutoFocusedRef = useRef(false);
  /** In-canvas contentEditable edit mode for the active text float (Phase 2b A). */
  const [textFloatEditMode, setTextFloatEditMode] = useState(false);
  const textFloatEditModeRef = useRef(false);
  /** @type {React.MutableRefObject<null|(() => boolean)>} */
  const textEditFlushRef = useRef(null);
  /** When re-editing a committed text layer, float writes back to this id. */
  const editingTextLayerIdRef = useRef(null);
  /** When re-transforming a committed raster layer, float writes back to this id. */
  const editingRasterLayerIdRef = useRef(null);
  /** Snapshot for Escape revert during text re-edit. */
  const textEditSnapshotRef = useRef(null);
  const rasterizeTextLayerIdRef = useRef(null);
  const [rasterizeTextDialogOpen, setRasterizeTextDialogOpen] = useState(false);
  const [clipboardReady, setClipboardReady] = useState(false);
  const [modalSize, setModalSize] = useState(DEFAULT_MODAL_SIZE);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectMode, setSelectMode] = useState(SELECT_RECT);
  const [selection, setSelection] = useState(null);
  const selectionRef = useRef(null);
  const [selectDraft, setSelectDraft] = useState(null);
  /** @type {[import('../../lib/imageEditorPath').EditorPath & { hover?: {x:number,y:number}, snapClose?: boolean }|null, Function]|any} */
  const [pathDraft, setPathDraft] = useState(null);
  const [selectTolerance, setSelectTolerance] = useState(32);
  /** Subtract from selection when Select brush is active (also Alt while painting). */
  const [selectEraseMode, setSelectEraseMode] = useState(false);
  /** Magnetic lasso edge-search radius (px). */
  const [magneticRadius, setMagneticRadius] = useState(DEFAULT_MAGNETIC_RADIUS);
  /** Flyout menu for Select / Inpaint sub-modes. */
  const [selectModeMenuAnchor, setSelectModeMenuAnchor] = useState(null);
  const [panelPropsOpen, setPanelPropsOpen] = useState(true);
  const [panelToolOpen, setPanelToolOpen] = useState(true);
  const [pencilMode, setPencilMode] = useState(false);
  const [colorSamples, setColorSamples] = useState([]);
  const [cropDraft, setCropDraft] = useState(null);
  const [retouchStrength, setRetouchStrength] = useState(
    DEFAULT_RETOUCH_STRENGTH,
  );
  const [spongeMode, setSpongeMode] = useState(
    /** @type {'saturate'|'desaturate'} */ ('saturate'),
  );
  const [cloneSource, setCloneSource] = useState(
    /** @type {{ x: number, y: number }|null} */ (null),
  );
  const [cloneSetSourceMode, setCloneSetSourceMode] = useState(false);
  const [gradientDraft, setGradientDraft] = useState(
    /** @type {{ x0: number, y0: number, x1: number, y1: number }|null} */ (
      null
    ),
  );
  const [optionsPanelWidth, setOptionsPanelWidth] = useState(() => {
    try {
      const raw = sessionStorage.getItem(OPTIONS_PANEL_WIDTH_STORAGE_KEY);
      const n = Number(raw);
      if (Number.isFinite(n)) {
        return Math.min(
          OPTIONS_PANEL_WIDTH_MAX,
          Math.max(OPTIONS_PANEL_WIDTH_MIN, Math.round(n)),
        );
      }
    } catch {
      /* ignore */
    }
    return OPTIONS_PANEL_WIDTH_DEFAULT;
  });
  const [textToolContent, setTextToolContent] = useState('');
  const [textFontFamily, setTextFontFamily] = useState(
    IMAGE_EDITOR_TEXT_FONTS[0],
  );
  const [textFontSize, setTextFontSize] = useState(32);
  const [textAlign, setTextAlign] = useState(
    /** @type {'left'|'center'|'right'} */ ('left'),
  );
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        OPTIONS_PANEL_WIDTH_STORAGE_KEY,
        String(optionsPanelWidth),
      );
    } catch {
      /* ignore */
    }
  }, [optionsPanelWidth]);

  useEffect(() => {
    floatObjRef.current = floatObj;
    // Keep dirty in sync when a float session starts/ends without a history bump.
    const doc = docRef.current;
    const floatPending = Boolean(floatObj);
    if (doc) {
      setDirty(doc.revision !== doc.persistedRevision || floatPending);
    } else {
      setDirty(floatPending);
    }
  }, [floatObj]);

  // In-canvas edit replaces Content autofocus when a text float is active.
  const textFloatActive = Boolean(floatObj && floatObj.origin === 'text');
  useEffect(() => {
    if (!textFloatActive) {
      textContentAutoFocusedRef.current = false;
      textFloatEditModeRef.current = false;
      setTextFloatEditMode(false);
    }
  }, [textFloatActive]);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  // Remove-background mode
  const [sampledColor, setSampledColor] = useState(null);
  const [tolerance, setTolerance] = useState(32);

  // Paint mode
  const [brushSize, setBrushSize] = useState(12);
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [activeColorSlot, setActiveColorSlot] = useState('fg');
  const [eraser, setEraser] = useState(false);
  const [eyeDropper, setEyeDropper] = useState(false);

  // Overlay chrome (session-only; not part of buffer/history/save)
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(16);
  /** Display-only: checkerboard vs solid gray behind transparent pixels. */
  const [showTransparencyCheckerboard, setShowTransparencyCheckerboard] =
    useState(true);
  const [showRulers, setShowRulers] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(false);
  /** Vertical guide X positions in canvas pixels. */
  const [vGuides, setVGuides] = useState([]);
  /** Horizontal guide Y positions in canvas pixels. */
  const [hGuides, setHGuides] = useState([]);

  // Viewport zoom / pan (CSS + scroll only — never mutates buffer pixels)
  const [zoom, setZoom] = useState(1);
  const [panMode, setPanMode] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [isPanning, setIsPanning] = useState(false);

  // Save dialog (flat image export + layered project)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveFilename, setSaveFilename] = useState('');
  const [saveKind, setSaveKind] = useState(
    /** @type {'image'|'project'} */ ('image'),
  );
  const [saveFormat, setSaveFormat] = useState(
    normalizeImageEditorFormat(preferredFormat),
  );
  const [jpegAlphaAck, setJpegAlphaAck] = useState(false);
  const [saveHasAlpha, setSaveHasAlpha] = useState(false);
  const [fileMenuAnchor, setFileMenuAnchor] = useState(null);
  const [editMenuAnchor, setEditMenuAnchor] = useState(null);
  const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
  const [selectMenuAnchor, setSelectMenuAnchor] = useState(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newWidth, setNewWidth] = useState(512);
  const [newHeight, setNewHeight] = useState(512);
  const [newFilename, setNewFilename] = useState('untitled');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateMode, setGenerateMode] = useState(GENERATE_MODE_TXT2IMG);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generateStrength, setGenerateStrength] = useState(
    DEFAULT_IMG2IMG_STRENGTH,
  );
  const [generateBusy, setGenerateBusy] = useState(false);
  const [generateError, setGenerateError] = useState('');
  /** Soften inpaint mask edges (1–2px dilate). Default OFF = exact selection. */
  const [generateSoftenMask, setGenerateSoftenMask] = useState(false);
  const [imageSizeDialogOpen, setImageSizeDialogOpen] = useState(false);
  const [imageWidth, setImageWidth] = useState(1);
  const [imageHeight, setImageHeight] = useState(1);
  const [lockImageAspect, setLockImageAspect] = useState(true);
  const [canvasSizeDialogOpen, setCanvasSizeDialogOpen] = useState(false);
  const [newCanvasWidth, setNewCanvasWidth] = useState(1);
  const [newCanvasHeight, setNewCanvasHeight] = useState(1);
  const [canvasAnchor, setCanvasAnchor] = useState('center');
  const autosaveTimerRef = useRef(/** @type {number|null} */ (null));
  const autosaveBusyRef = useRef(false);
  const performAutosaveRef = useRef(
    /** @type {null|(() => Promise<boolean>)} */ (null),
  );
  const autosaveNodeIdRef = useRef(/** @type {string|null} */ (null));

  const saveFormatMeta = getImageEditorFormatMeta(saveFormat);
  const savePreview = buildSaveFilePreview(saveFilename, saveFormat);
  const jpegNeedsAlphaWarning =
    saveFormat === 'jpeg' && saveHasAlpha && !jpegAlphaAck;
  const panActive = panMode || spaceHeld;

  const bumpLayersUi = useCallback(() => {
    setLayersEpoch((n) => n + 1);
  }, []);

  const syncHistoryUi = useCallback(() => {
    const hist = historyRef.current;
    const doc = docRef.current;
    const idx = hist.index;
    const len = hist.stack.length;
    setCanUndo(idx > 0);
    setCanRedo(idx >= 0 && idx < len - 1);
    // Uncommitted float / in-canvas text edit counts as dirty for idle warn
    // and debounce, even though autosave defers capturing that float.
    const floatPending = Boolean(floatObjRef.current);
    if (doc) {
      setDirty(doc.revision !== doc.persistedRevision || floatPending);
    } else {
      setDirty(idx > 0 || floatPending);
    }
    bumpLayersUi();
  }, [bumpLayersUi]);

  const presentDoc = useCallback(() => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    if (!doc || !canvas) return;
    const omitLayerIds = [];
    if (
      editingTextLayerIdRef.current &&
      floatObjRef.current?.origin === 'text'
    ) {
      omitLayerIds.push(editingTextLayerIdRef.current);
    }
    if (editingRasterLayerIdRef.current && floatObjRef.current) {
      omitLayerIds.push(editingRasterLayerIdRef.current);
    }
    presentEditorDocument(doc, canvas, { force: true, omitLayerIds });
    setCanvasSize({ width: doc.width, height: doc.height });
  }, []);

  /**
   * Push CoW document snapshot (structural sharing of unchanged layer pixels).
   */
  const pushHistory = useCallback(() => {
    const doc = docRef.current;
    if (!doc) return;
    pushEditorDocumentHistory(historyRef.current, doc);
    syncHistoryUi();
  }, [syncHistoryUi]);

  /**
   * @returns {CanvasRenderingContext2D|null}
   */
  const getCtx = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d', { willReadFrequently: true });
  }, []);

  const promptRasterizeActiveTextLayer = useCallback(() => {
    const doc = docRef.current;
    const active = getActiveEditorLayer(doc);
    if (!active || !isEditorTextLayer(active)) return false;
    rasterizeTextLayerIdRef.current = active.id;
    setRasterizeTextDialogOpen(true);
    return true;
  }, []);

  /**
   * Active-layer pixels (clone). Legacy tools mutate this then writeWorking.
   * @returns {ImageData|null}
   */
  const readWorking = useCallback(() => {
    const doc = docRef.current;
    const active = getActiveEditorLayer(doc);
    if (!active?.pixels) return null;
    if (isEditorTextLayer(active)) {
      promptRasterizeActiveTextLayer();
      return null;
    }
    try {
      return cloneImageData(active.pixels);
    } catch (err) {
      console.error('ImageEditorModal: readWorking failed', err);
      setToolError(PIXEL_READ_ERROR);
      return null;
    }
  }, [promptRasterizeActiveTextLayer]);

  /**
   * Write pixels into the active layer (same size). Size-changing paths are
   * reserved for legacy callers; Crop / Canvas Size / Image Size use
   * document-level helpers that keep the full layer stack.
   * @param {ImageData} imageData
   */
  const writeWorking = useCallback(
    (imageData) => {
      const doc = docRef.current;
      if (!doc || !imageData) return;
      const active = getActiveEditorLayer(doc);
      if (active && isEditorTextLayer(active)) {
        promptRasterizeActiveTextLayer();
        return;
      }
      if (imageData.width !== doc.width || imageData.height !== doc.height) {
        replaceEditorDocumentFlat(doc, imageData);
      } else {
        if (!active) return;
        const unique = ensureLayerPixelsUnique(doc, active.id);
        unique.pixels.data.set(imageData.data);
        doc.compositeDirty = true;
      }
      presentDoc();
    },
    [presentDoc, promptRasterizeActiveTextLayer],
  );

  const discardFloat = useCallback(() => {
    const prev = floatObjRef.current;
    if (!prev) return;
    textFloatEditModeRef.current = false;
    setTextFloatEditMode(false);
    const doc = docRef.current;
    const editingId = editingTextLayerIdRef.current;
    const snapshot = textEditSnapshotRef.current;
    if (doc && editingId && snapshot && prev.origin === 'text') {
      patchEditorTextLayer(doc, editingId, snapshot);
      editingTextLayerIdRef.current = null;
      textEditSnapshotRef.current = null;
      presentDoc();
    } else if (prev.origin === 'transform-canvas' && doc) {
      replaceEditorDocumentFlat(doc, prev.imageData);
      presentDoc();
    }
    // Raster re-transform: layer pixels were never mutated (omitted while
    // floating) — clearing the id restores them on the next present.
    editingRasterLayerIdRef.current = null;
    floatObjRef.current = null;
    setFloatObj(null);
    floatDragRef.current = null;
  }, [presentDoc]);

  const commitFloat = useCallback(() => {
    // Slice C: final DOM→runs sync before bake/commit (all commit paths).
    if (textFloatEditModeRef.current && typeof textEditFlushRef.current === 'function') {
      textEditFlushRef.current();
    }
    const prev = floatObjRef.current;
    const doc = docRef.current;
    if (!prev || !doc) return false;
    textFloatEditModeRef.current = false;
    setTextFloatEditMode(false);

    if (prev.origin === 'text' && prev.textParams) {
      const content = editorTextLayerContentFromFloat(prev);
      const name = layerNameFromFloatOrigin(prev.origin, {
        labelHint: prev.labelHint,
      });
      const editingId = editingTextLayerIdRef.current;
      if (editingId) {
        patchEditorTextLayer(doc, editingId, content);
      } else {
        addEditorTextLayer(doc, {
          name,
          text: content,
          insertAboveActive: true,
        });
      }
      editingTextLayerIdRef.current = null;
      textEditSnapshotRef.current = null;
      editingRasterLayerIdRef.current = null;
      pushHistory();
      floatObjRef.current = null;
      setFloatObj(null);
      floatDragRef.current = null;
      presentDoc();
      return true;
    }

    const pixels = rasterizeFloatToDocumentPixels(prev, doc.width, doc.height);
    if (!pixels) {
      setToolError(PIXEL_READ_ERROR);
      return false;
    }
    const editingRasterId = editingRasterLayerIdRef.current;
    if (editingRasterId) {
      if (!setEditorLayerPixels(doc, editingRasterId, pixels)) {
        setToolError(PIXEL_READ_ERROR);
        return false;
      }
      editingRasterLayerIdRef.current = null;
    } else {
      const name = layerNameFromFloatOrigin(prev.origin, {
        labelHint: prev.labelHint,
      });
      addEditorLayer(doc, { name, pixels, insertAboveActive: true });
    }
    pushHistory();
    floatObjRef.current = null;
    setFloatObj(null);
    floatDragRef.current = null;
    presentDoc();
    return true;
  }, [presentDoc, pushHistory]);

  const confirmRasterizeTextLayer = useCallback(() => {
    const doc = docRef.current;
    const layerId = rasterizeTextLayerIdRef.current;
    if (!doc || !layerId) {
      setRasterizeTextDialogOpen(false);
      return;
    }
    if (rasterizeEditorTextLayer(doc, layerId)) {
      pushHistory();
      presentDoc();
    }
    rasterizeTextLayerIdRef.current = null;
    setRasterizeTextDialogOpen(false);
  }, [presentDoc, pushHistory]);

  /**
   * Enter in-canvas text edit (contentEditable). Resets rotation to 0°.
   */
  const enterTextFloatEditMode = useCallback(() => {
    const float = floatObjRef.current;
    if (!float || float.origin !== 'text' || !float.textParams) return;
    if (Math.abs(Number(float.rotationDeg) || 0) > 0.001) {
      const next = { ...float, rotationDeg: 0 };
      floatObjRef.current = next;
      setFloatObj(next);
      setToolError('Rotation reset to 0° for in-canvas text editing.');
    }
    textFloatEditModeRef.current = true;
    setTextFloatEditMode(true);
  }, []);

  /** Leave in-canvas edit without committing/discarding the float. */
  const exitTextFloatEditMode = useCallback(() => {
    if (typeof textEditFlushRef.current === 'function') {
      textEditFlushRef.current();
    }
    textFloatEditModeRef.current = false;
    setTextFloatEditMode(false);
  }, []);

  const registerTextEditFlush = useCallback((flush) => {
    textEditFlushRef.current = typeof flush === 'function' ? flush : null;
  }, []);

  const beginTextLayerEdit = useCallback(
    (layerId) => {
      if (busy) return;
      const doc = docRef.current;
      if (!doc) return;
      const layer = doc.layers.find((l) => l.id === layerId);
      if (!isEditorTextLayer(layer)) return;
      if (
        floatObjRef.current &&
        editingTextLayerIdRef.current &&
        editingTextLayerIdRef.current !== layerId
      ) {
        commitFloat();
      }
      if (
        floatObjRef.current &&
        editingTextLayerIdRef.current === layerId
      ) {
        enterTextFloatEditMode();
        return;
      }
      textEditSnapshotRef.current = cloneEditorTextLayerContent(layer.text);
      editingTextLayerIdRef.current = layerId;
      editingRasterLayerIdRef.current = null;
      setActiveEditorLayer(doc, layerId);
      const float = editorTextLayerToFloat(layer);
      if (!float) {
        editingTextLayerIdRef.current = null;
        textEditSnapshotRef.current = null;
        return;
      }
      floatObjRef.current = float;
      setFloatObj(float);
      if (float.textParams) {
        setTextToolContent(float.textParams.text);
        setTextFontFamily(float.textParams.fontFamily);
        setTextFontSize(float.textParams.fontSize);
        setTextAlign(float.textParams.align);
        setTextBold(Boolean(float.textParams.bold));
        setTextItalic(Boolean(float.textParams.italic));
      }
      setTool(TOOL_MOVE);
      setToolError('');
      bumpLayersUi();
      presentDoc();
      // Enter edit in the same event turn as setFloatObj (handles stay visible
      // during edit — Slice D / "handles always on").
      enterTextFloatEditMode();
    },
    [busy, bumpLayersUi, commitFloat, enterTextFloatEditMode, presentDoc],
  );

  /**
   * Lift a committed raster layer into a transform float; commit writes back
   * to the same layer id (does not add a new layer).
   * @param {string} layerId
   */
  const beginRasterLayerTransform = useCallback(
    (layerId) => {
      if (busy) return;
      const doc = docRef.current;
      if (!doc) return;
      const layer = doc.layers.find((l) => l.id === layerId);
      if (!layer || isEditorTextLayer(layer)) return;
      if (floatObjRef.current) {
        commitFloat();
      }
      const extracted = extractOpaqueContentFloat(layer.pixels);
      if (!extracted) {
        setToolError('This layer has no visible pixels to transform.');
        return;
      }
      editingTextLayerIdRef.current = null;
      textEditSnapshotRef.current = null;
      editingRasterLayerIdRef.current = layerId;
      setActiveEditorLayer(doc, layerId);
      const float = createFloat({
        imageData: extracted.imageData,
        cx: extracted.cx,
        cy: extracted.cy,
        scale: 1,
        rotationDeg: 0,
        origin: 'lift',
        labelHint: layer.name,
      });
      floatObjRef.current = float;
      setFloatObj(float);
      textFloatEditModeRef.current = false;
      setTextFloatEditMode(false);
      setTool(TOOL_MOVE);
      setToolError('');
      bumpLayersUi();
      presentDoc();
    },
    [busy, bumpLayersUi, commitFloat, presentDoc],
  );

  /**
   * Commit any existing float, then set next (at most one float).
   * @param {import('../../lib/imageEditorFloat').EditorFloat} next
   */
  const replaceFloat = useCallback(
    (next) => {
      if (floatObjRef.current) {
        commitFloat();
      }
      floatObjRef.current = next;
      setFloatObj(next);
    },
    [commitFloat],
  );

  /**
   * Live-update an uncommitted text float's content/style (re-rasterize pixels).
   * @param {Partial<{ text: string, fontFamily: string, fontSize: number, color: string, align: 'left'|'center'|'right', bold: boolean, italic: boolean, runs: import('../../lib/imageEditorText').EditorTextRun[] }>} patch
   */
  const patchActiveTextFloat = useCallback((patch) => {
    const prev = floatObjRef.current;
    if (!prev || prev.origin !== 'text') return;
    // Use WithLayout so textLayout cache is retained on the float (Slice C).
    const next = applyTextFloatContentPatch(
      prev,
      patch,
      renderTextToImageDataWithLayout,
    );
    if (!next) return;
    floatObjRef.current = next;
    setFloatObj(next);
    if (patch.text !== undefined) setTextToolContent(String(patch.text));
    if (patch.fontFamily !== undefined) setTextFontFamily(String(patch.fontFamily));
    if (patch.fontSize !== undefined) {
      setTextFontSize(
        Math.max(8, Math.min(256, Math.round(Number(patch.fontSize) || 32))),
      );
    }
    if (patch.align === 'left' || patch.align === 'center' || patch.align === 'right') {
      setTextAlign(patch.align);
    }
    if (patch.bold !== undefined) setTextBold(Boolean(patch.bold));
    if (patch.italic !== undefined) setTextItalic(Boolean(patch.italic));
  }, []);

  const promoteWholeCanvasToFloat = useCallback(() => {
    if (floatObjRef.current) {
      commitFloat();
    }
    const doc = docRef.current;
    if (!doc) return;
    const composite = compositeEditorDocument(doc);
    const next = createFloat({
      imageData: composite,
      cx: doc.width / 2,
      cy: doc.height / 2,
      scale: 1,
      rotationDeg: 0,
      origin: 'transform-canvas',
    });
    replaceEditorDocumentFlat(doc, new ImageData(doc.width, doc.height));
    presentDoc();
    floatObjRef.current = next;
    setFloatObj(next);
    setToolError('');
  }, [commitFloat, presentDoc]);

  const copyToModalClipboard = useCallback(() => {
    const selected = selectionRef.current;
    if (selected && selectionHasPixels(selected)) {
      const working = readWorking();
      const extracted = working
        ? extractSelectionImageData(working, selected)
        : null;
      if (!extracted) return;
      clipboardRef.current = cloneFloatPixels(extracted.imageData);
      setClipboardReady(true);
      setToolError('');
      return;
    }
    const active = floatObjRef.current;
    if (active) {
      clipboardRef.current = cloneFloatPixels(active.imageData);
      setClipboardReady(true);
      setToolError('');
      return;
    }
    const working = readWorking();
    if (!working) return;
    clipboardRef.current = cloneFloatPixels(working);
    setClipboardReady(true);
    setToolError('');
  }, [readWorking]);

  const clearSelection = useCallback(() => {
    selectionRef.current = null;
    setSelection(null);
    setSelectDraft(null);
    polyActiveRef.current = false;
    lassoPointsRef.current = [];
    magneticMagRef.current = null;
  }, []);

  const clearPathDraft = useCallback(() => {
    pathRef.current = createEmptyEditorPath();
    penDragRef.current = false;
    setPathDraft(null);
  }, []);

  /**
   * Sync overlay from pathRef.
   * @param {{ hover?: {x:number,y:number}|null }} [opts]
   */
  const syncPathDraft = useCallback((opts = {}) => {
    const path = pathRef.current;
    if (!path?.anchors?.length) {
      setPathDraft(null);
      return;
    }
    const hover = opts.hover || null;
    const snapClose = Boolean(
      hover && pathNearFirstAnchor(path, hover.x, hover.y),
    );
    setPathDraft({
      type: 'path',
      path: {
        anchors: path.anchors.map((a) => ({ ...a })),
        closed: path.closed,
      },
      hover: path.closed ? null : hover,
      snapClose,
    });
  }, []);

  const convertPathToSelection = useCallback(() => {
    const canvas = canvasRef.current;
    const path = pathRef.current;
    if (!canvas || !path?.closed) return false;
    const next = pathToSelectionMask(canvas.width, canvas.height, path);
    if (!next) return false;
    selectionRef.current = next;
    setSelection(next);
    clearPathDraft();
    setToolError('');
    return true;
  }, [clearPathDraft]);

  const rasterizePathToLayer = useCallback(() => {
    const path = pathRef.current;
    if (!path?.closed) return false;
    if (floatObjRef.current) commitFloat();
    const raster = rasterizePathToImageData(path, {
      color: foregroundColor,
      padding: 2,
    });
    if (!raster) {
      setToolError('Could not rasterize the path.');
      return false;
    }
    const next = createFloat({
      imageData: raster.imageData,
      cx: raster.offsetX + raster.imageData.width / 2,
      cy: raster.offsetY + raster.imageData.height / 2,
      origin: 'path',
    });
    replaceFloat(next);
    clearPathDraft();
    setTool(TOOL_MOVE);
    setToolError('');
    return true;
  }, [clearPathDraft, commitFloat, foregroundColor, replaceFloat]);

  const closePolygonalSelection = useCallback(() => {
    const canvas = canvasRef.current;
    const points = lassoPointsRef.current;
    if (!canvas || !points || points.length < 3) {
      // Keep the in-progress draft — do not wipe on a failed close gesture.
      return false;
    }
    const next = lassoSelectionMask(canvas.width, canvas.height, points);
    selectionRef.current = next;
    setSelection(next);
    polyActiveRef.current = false;
    lassoPointsRef.current = [];
    setSelectDraft(null);
    return true;
  }, []);

  const liftSelection = useCallback(() => {
    const selected = selectionRef.current;
    if (!selected || !selectionHasPixels(selected)) return;
    if (floatObjRef.current) commitFloat();
    const working = readWorking();
    if (!working) return;
    const extracted = extractSelectionImageData(working, selected);
    if (!extracted) return;
    const cleared = clearSelectionPixels(working, selected);
    const next = createFloat({
      imageData: extracted.imageData,
      cx: extracted.offsetX + extracted.imageData.width / 2,
      cy: extracted.offsetY + extracted.imageData.height / 2,
      origin: 'lift',
    });
    writeWorking(cleared);
    pushHistory();
    floatObjRef.current = next;
    setFloatObj(next);
    clearSelection();
    setToolError('');
  }, [clearSelection, commitFloat, pushHistory, readWorking, writeWorking]);

  const pasteClipboardAsFloat = useCallback(() => {
    const clip = clipboardRef.current;
    const canvas = canvasRef.current;
    if (!clip || !canvas) return;
    const anchor = lastCanvasPtRef.current;
    const cx = anchor?.x ?? canvas.width / 2;
    const cy = anchor?.y ?? canvas.height / 2;
    const next = createFloat({
      imageData: clip,
      cx,
      cy,
      scale: 1,
      rotationDeg: 0,
      origin: 'paste',
    });
    replaceFloat(next);
    setToolError('');
  }, [replaceFloat]);

  /**
   * @param {number} clientX
   * @param {number} clientY
   * @returns {{x:number,y:number}|null}
   */
  const clientToCanvas = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: Math.max(
        0,
        Math.min(canvas.width - 1, Math.round((clientX - rect.left) * scaleX)),
      ),
      y: Math.max(
        0,
        Math.min(canvas.height - 1, Math.round((clientY - rect.top) * scaleY)),
      ),
    };
  }, []);

  /**
   * Canvas coords for tools. Paint respects snap; sampling does not.
   * @param {number} clientX
   * @param {number} clientY
   * @param {{applySnap?: boolean}} [opts]
   * @returns {{x:number,y:number}|null}
   */
  const clientToToolPoint = useCallback(
    (clientX, clientY, opts = {}) => {
      const pt = clientToCanvas(clientX, clientY);
      if (!pt) return null;
      if (!opts.applySnap) return pt;
      const canvas = canvasRef.current;
      if (!canvas) return pt;
      return snapCanvasPoint(pt, {
        snapEnabled,
        showGrid,
        gridSize,
        vGuides,
        hGuides,
        width: canvas.width,
        height: canvas.height,
      });
    },
    [clientToCanvas, snapEnabled, showGrid, gridSize, vGuides, hGuides],
  );

  const resetDocumentChrome = useCallback(() => {
    setLoadError('');
    setToolError('');
    setImageReady(false);
    setSaveInFlight(false);
    setAutosaveStatus('idle');
    setDirty(false);
    setCanUndo(false);
    setCanRedo(false);
    setSampledColor(null);
    setTool(TOOL_MOVE);
    setEraser(false);
    setEyeDropper(false);
    setPencilMode(false);
    setSelectMode(SELECT_RECT);
    setSelection(null);
    setSelectDraft(null);
    setSelectTolerance(32);
    setSelectEraseMode(false);
    setMagneticRadius(DEFAULT_MAGNETIC_RADIUS);
    setSelectModeMenuAnchor(null);
    polyActiveRef.current = false;
    magneticMagRef.current = null;
    pathRef.current = createEmptyEditorPath();
    penDragRef.current = false;
    setPathDraft(null);
    setColorSamples([]);
    setCropDraft(null);
    setCloneSource(null);
    setCloneSetSourceMode(false);
    setGradientDraft(null);
    setRetouchStrength(DEFAULT_RETOUCH_STRENGTH);
    setSpongeMode('saturate');
    setFullscreen(false);
    setModalSize(DEFAULT_MODAL_SIZE);
    setCanvasSize({ width: 0, height: 0 });
    setShowGrid(false);
    setGridSize(16);
    setShowRulers(false);
    setSnapEnabled(false);
    setVGuides([]);
    setHGuides([]);
    setZoom(1);
    setPanMode(false);
    setSpaceHeld(false);
    setIsPanning(false);
    setSaveDialogOpen(false);
    setSaveFilename('');
    setSaveKind('image');
    setFloatObj(null);
    setClipboardReady(false);
    textFloatEditModeRef.current = false;
    setTextFloatEditMode(false);
    draggingGuideRef.current = null;
    panDragRef.current = null;
    spaceHeldRef.current = false;
    zHeldRef.current = false;
    floatDragRef.current = null;
    selectDragRef.current = null;
    cropDragRef.current = null;
    lassoPointsRef.current = [];
    selectionRef.current = null;
    lastCanvasPtRef.current = null;
    clipboardRef.current = null;
    pristineSnapRef.current = null;
    docRef.current = null;
    historyRef.current = createEditorDocumentHistory();
    paintingRef.current = false;
    strokeDirtyRef.current = false;
    layerPaintSeededRef.current = false;
    lastPaintPtRef.current = null;
    autosaveBusyRef.current = false;
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  /**
   * Install a loaded/created document into session state (canvas present happens
   * after imageReady so the editor shell only mounts once content is ready).
   * @param {import('../../lib/imageEditorDocument').EditorDocument} nextDoc
   */
  const installEditorDocument = useCallback((nextDoc) => {
    if (!nextDoc?.layers?.length) return false;
    docRef.current = nextDoc;
    pristineSnapRef.current = snapshotEditorDocument(nextDoc);
    historyRef.current = createEditorDocumentHistory();
    pushEditorDocumentHistory(historyRef.current, nextDoc);
    historyRef.current.index = 0;
    clearCowUniqueLayerIds(nextDoc);
    setCanvasSize({ width: nextDoc.width, height: nextDoc.height });
    fitZoomPendingRef.current = true;
    setImageReady(true);
    syncHistoryUi();
    return true;
  }, [syncHistoryUi]);

  /**
   * Fit the full canvas into the current viewport (CSS zoom). Manual zoom controls
   * remain independent after this one-shot open fit.
   */
  const fitZoomToViewport = useCallback(() => {
    const viewport = viewportRef.current;
    const doc = docRef.current;
    if (!viewport || !doc) return;
    // Match viewport sx padding (px:1 / py:2) + small breathing room.
    const padX = 8 + 8;
    const padY = 16 + 8;
    const ruler = showRulers ? RULER_SIZE_PX : 0;
    const availW = Math.max(1, viewport.clientWidth - padX * 2 - ruler);
    const availH = Math.max(1, viewport.clientHeight - padY * 2 - ruler);
    const next = computeFitZoom(doc.width, doc.height, availW, availH);
    setZoom(next);
    viewport.scrollLeft = 0;
    viewport.scrollTop = 0;
  }, [showRulers]);

  /**
   * Start a fresh editable document from already-readable pixel data.
   * Flat open → single Background layer (no asset migration).
   * @param {ImageData} imageData
   */
  const bootstrapCanvasFromImageData = useCallback((imageData) => {
    if (!imageData) return false;
    const doc = createEditorDocumentFromImageData(imageData, {
      name: 'Background',
    });
    return installEditorDocument(doc);
  }, [installEditorDocument]);

  /**
   * @param {import('../../lib/imageEditorDocument').EditorDocument} nextDoc
   */
  const bootstrapCanvasFromEditorDocument = useCallback((nextDoc) => {
    return installEditorDocument(nextDoc);
  }, [installEditorDocument]);

  const bootstrapBlankCanvas = useCallback((width, height) => {
    const safeWidth = Math.max(1, Math.min(MAX_CANVAS_DIMENSION, Math.round(Number(width) || 1)));
    const safeHeight = Math.max(1, Math.min(MAX_CANVAS_DIMENSION, Math.round(Number(height) || 1)));
    const pixels = new ImageData(safeWidth, safeHeight);
    resetDocumentChrome();
    return bootstrapCanvasFromImageData(pixels);
  }, [bootstrapCanvasFromImageData, resetDocumentChrome]);

  // Paint installed document onto the canvas once the editor shell mounts.
  useLayoutEffect(() => {
    if (!open || !imageReady) return;
    const canvas = canvasRef.current;
    const doc = docRef.current;
    if (!canvas || !doc) return;
    presentEditorDocument(doc, canvas, { force: true });
  }, [open, imageReady, canvasSize.width, canvasSize.height]);

  // One-shot fit-to-viewport after open/load/create (not on every resize).
  useLayoutEffect(() => {
    if (!open || !imageReady || !fitZoomPendingRef.current) return undefined;
    let cancelled = false;
    let frames = 0;
    const tryFit = () => {
      if (cancelled || !fitZoomPendingRef.current) return;
      const viewport = viewportRef.current;
      frames += 1;
      if (
        !viewport ||
        viewport.clientWidth < 40 ||
        viewport.clientHeight < 40
      ) {
        if (frames < 30) requestAnimationFrame(tryFit);
        return;
      }
      fitZoomPendingRef.current = false;
      fitZoomToViewport();
    };
    const id = requestAnimationFrame(tryFit);
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [open, imageReady, canvasSize.width, canvasSize.height, fitZoomToViewport]);

  // Idle-session: expose dirty state + autosave flush for logout warning.
  useEffect(() => {
    if (!open) return undefined;
    return registerIdleUnsavedWorkChecker(() => ({
      dirty: Boolean(dirty),
      label: 'Image Editor',
      flush:
        typeof onAutosaveProject === 'function'
          ? () => performAutosaveRef.current?.()
          : undefined,
    }));
  }, [open, dirty, onAutosaveProject]);

  // Long-running AI generate/inpaint counts as activity for idle timeout.
  useEffect(() => {
    if (!open || !generateBusy) return undefined;
    reportAppActivity({ reason: 'image-editor-ai' });
    const id = window.setInterval(() => {
      reportAppActivity({ reason: 'image-editor-ai' });
    }, IDLE_BUSY_HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [open, generateBusy]);

  // Save / hub busy (upload) also counts as activity.
  useEffect(() => {
    if (!open || !busy) return undefined;
    reportAppActivity({ reason: 'image-editor-busy' });
    const id = window.setInterval(() => {
      reportAppActivity({ reason: 'image-editor-busy' });
    }, IDLE_BUSY_HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [open, busy]);

  const performAutosave = useCallback(async () => {
    if (!open || typeof onAutosaveProject !== 'function') return false;
    if (autosaveBusyRef.current) return false;
    if (busy || generateBusy || saveInFlight) return false;
    // Never force-commit an active float / text-edit session. Explicit File →
    // Save still commits-then-saves; silent autosave only persists the last
    // committed document and defers until the user commits the float.
    const doc = docRef.current;
    if (!doc) return false;
    if (doc.revision === doc.persistedRevision) return false;
    autosaveBusyRef.current = true;
    setAutosaveStatus('saving');
    try {
      const result = await onAutosaveProject({
        document: doc,
        fileStem: assetLabel || 'autosave',
        nodeId: autosaveNodeIdRef.current || null,
        kind: IMAGE_EDITOR_PROJECT_KIND,
      });
      if (result?.nodeId) autosaveNodeIdRef.current = result.nodeId;
      doc.persistedRevision = doc.revision;
      syncHistoryUi();
      setAutosaveStatus('idle');
      return true;
    } catch (err) {
      console.warn('ImageEditor autosave failed:', err);
      setAutosaveStatus('error');
      setToolError(err?.message || 'Autosave failed.');
      return false;
    } finally {
      autosaveBusyRef.current = false;
    }
  }, [
    open,
    onAutosaveProject,
    busy,
    generateBusy,
    saveInFlight,
    assetLabel,
    syncHistoryUi,
  ]);

  useEffect(() => {
    performAutosaveRef.current = performAutosave;
  }, [performAutosave]);

  // Debounced autosave (10s) for layered project state.
  // Re-arms when an active float ends so the post-commit revision is captured
  // on the next cycle (autosave never force-commits the float itself).
  useEffect(() => {
    if (!open || !dirty || typeof onAutosaveProject !== 'function') return undefined;
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      void performAutosave();
    }, 10000);
    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [open, dirty, floatObj, onAutosaveProject, performAutosave]);

  // Hidden tab / unload best-effort autosave.
  useEffect(() => {
    if (!open || typeof onAutosaveProject !== 'function') return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void performAutosave();
      }
    };
    const onBeforeUnload = () => {
      void performAutosave();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [open, onAutosaveProject, performAutosave]);

  // Load the externally selected image, or bootstrap the hub's blank canvas.
  // useLayoutEffect: Dialog mounts the <canvas> in the same commit; useEffect
  // can still see canvasRef=null on the first open (grayed-out tools forever).
  useLayoutEffect(() => {
    if (!open) return undefined;
    const blankW = blankSize?.width;
    const blankH = blankSize?.height;
    const hasLayeredNode = typeof layeredNodeId === 'string' && layeredNodeId;
    if (!hasLayeredNode && !imageUrl && !(blankW && blankH)) return undefined;

    let cancelled = false;
    resetDocumentChrome();
    autosaveNodeIdRef.current = null;

    const runBlankBootstrap = () => {
      if (cancelled) return;
      const ok = bootstrapBlankCanvas(blankW, blankH);
      if (!ok && !cancelled) {
        setLoadError('Could not create a blank canvas in this browser.');
      }
    };

    if (hasLayeredNode) {
      const runLayeredLoad = async () => {
        try {
          if (!companyId) {
            throw new Error('Company is required to open layered projects.');
          }
          const loaded = await loadLayeredEditorProject({
            companyId,
            nodeId: layeredNodeId,
          });
          if (cancelled) return;
          const ok = bootstrapCanvasFromEditorDocument(loaded.document);
          if (!ok && !cancelled) {
            setLoadError('Could not restore the layered project.');
          } else if (ok && layeredNodeId.startsWith('autosave_')) {
            autosaveNodeIdRef.current = layeredNodeId;
          }
        } catch (err) {
          console.error('ImageEditorModal: layered load failed', err);
          if (!cancelled) {
            setLoadError(err?.message || 'Could not open the layered project.');
          }
        }
      };
      void runLayeredLoad();
      return () => {
        cancelled = true;
      };
    }

    if (!imageUrl) {
      runBlankBootstrap();
      return () => {
        cancelled = true;
      };
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (cancelled) return;

      const scale = Math.min(
        1,
        MAX_CANVAS_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight, 1),
      );
      const width = Math.max(1, Math.round(img.naturalWidth * scale));
      const height = Math.max(1, Math.round(img.naturalHeight * scale));
      const temp = document.createElement('canvas');
      temp.width = width;
      temp.height = height;
      const ctx = temp.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        setLoadError('Could not prepare the image in this browser.');
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      let imageData;
      try {
        imageData = ctx.getImageData(0, 0, width, height);
      } catch (err) {
        console.error('ImageEditorModal: getImageData failed', err);
        setLoadError(PIXEL_READ_ERROR);
        return;
      }
      const ok = bootstrapCanvasFromImageData(imageData);
      if (!ok && !cancelled) {
        setLoadError('Could not open the image.');
      }
    };

    img.onerror = () => {
      if (cancelled) return;
      setLoadError('Could not load the source image from its URL.');
    };

    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [
    open,
    imageUrl,
    blankSize?.width,
    blankSize?.height,
    layeredNodeId,
    companyId,
    bootstrapBlankCanvas,
    bootstrapCanvasFromEditorDocument,
    bootstrapCanvasFromImageData,
    resetDocumentChrome,
  ]);

  const handleUndo = useCallback(() => {
    if (floatObjRef.current) {
      discardFloat();
      return;
    }
    const doc = docRef.current;
    if (!doc) return;
    if (!undoEditorDocumentHistory(historyRef.current, doc)) return;
    presentDoc();
    syncHistoryUi();
  }, [presentDoc, syncHistoryUi, discardFloat]);

  const handleRedo = useCallback(() => {
    if (floatObjRef.current) {
      discardFloat();
      return;
    }
    const doc = docRef.current;
    if (!doc) return;
    if (!redoEditorDocumentHistory(historyRef.current, doc)) return;
    presentDoc();
    syncHistoryUi();
  }, [presentDoc, syncHistoryUi, discardFloat]);

  const adjustZoom = useCallback((next) => {
    setZoom(clampZoom(next));
  }, []);

  // Keyboard: undo/redo, space-pan, Z-hold for scroll-zoom.
  useEffect(() => {
    if (!open) return undefined;

    /**
     * @param {EventTarget|null} target
     */
    const isTypingTarget = (target) => {
      if (!target || !(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target.isContentEditable
      );
    };

    const onKeyDown = (event) => {
      if (event.code === 'Space' && !isTypingTarget(event.target)) {
        if (!spaceHeldRef.current) {
          spaceHeldRef.current = true;
          setSpaceHeld(true);
        }
        event.preventDefault();
        return;
      }
      if (event.key === 'z' || event.key === 'Z') {
        if (!event.ctrlKey && !event.metaKey && !isTypingTarget(event.target)) {
          zHeldRef.current = true;
        }
      }

      if (isTypingTarget(event.target)) {
        // contentEditable is a typing target — without this exception,
        // Ctrl/Cmd+Enter would be swallowed (same class as Content-field /
        // plain-Enter gates). Plain Enter = newline (browser default).
        if (
          event.key === 'Enter' &&
          (event.ctrlKey || event.metaKey) &&
          !event.altKey &&
          !event.shiftKey &&
          floatObjRef.current &&
          isTextFloatEditSurfaceTarget(event.target)
        ) {
          event.preventDefault();
          commitFloatFromPointer();
        }
        return;
      }
      if (busy || !imageReady) return;

      if (
        event.key === 'Enter' &&
        polyActiveRef.current &&
        isSelectionBuildingTool(tool) &&
        selectMode === SELECT_POLY
      ) {
        event.preventDefault();
        if (lassoPointsRef.current.length >= 3) {
          closePolygonalSelection();
        }
        return;
      }
      // Pen path close takes priority over float commit when an open path exists.
      if (
        event.key === 'Enter' &&
        tool === TOOL_PEN &&
        pathRef.current?.anchors?.length >= 3 &&
        !pathRef.current.closed
      ) {
        event.preventDefault();
        const closed = closeEditorPath(pathRef.current);
        if (closed) {
          pathRef.current = closed;
          syncPathDraft();
        }
        return;
      }
      if (event.key === 'Enter' && floatObjRef.current) {
        event.preventDefault();
        commitFloat();
        return;
      }
      if (event.key === 'Escape' && floatObjRef.current) {
        event.preventDefault();
        discardFloat();
        return;
      }
      if (event.key === 'Escape' && pathRef.current?.anchors?.length) {
        event.preventDefault();
        clearPathDraft();
        return;
      }
      if (event.key === 'Escape' && polyActiveRef.current) {
        event.preventDefault();
        polyActiveRef.current = false;
        lassoPointsRef.current = [];
        setSelectDraft(null);
        return;
      }
      if (event.key === 'Escape' && selectionRef.current) {
        event.preventDefault();
        clearSelection();
        return;
      }
      if (event.key === 'Escape' && cropDraft) {
        event.preventDefault();
        setCropDraft(null);
        return;
      }

      // Delete / Backspace: discard uncommitted float (same as Esc), else
      // delete the active committed layer. Never here while typing — the
      // isTypingTarget early-return above already covers INPUT/TEXTAREA/
      // contentEditable (in-canvas text caret). Extra text-edit-mode guard
      // covers focus-left-surface edge cases without inventing a new discard.
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        if (
          textFloatEditModeRef.current ||
          isTextFloatEditSurfaceTarget(event.target)
        ) {
          return;
        }
        if (floatObjRef.current) {
          event.preventDefault();
          discardFloat();
          return;
        }
        const doc = docRef.current;
        if (!doc?.activeLayerId) return;
        event.preventDefault();
        // Match Layers panel: no confirm dialog; last-layer guard via deleteEditorLayer.
        const result = deleteEditorLayer(doc, doc.activeLayerId);
        if (!result.ok) {
          setToolError(result.reason);
          return;
        }
        pushHistory();
        presentDoc();
        return;
      }

      const mod = event.ctrlKey || event.metaKey;
      if (!mod) return;
      if (saveDialogOpen) return;

      const key = event.key.toLowerCase();
      if (key === 'c') {
        event.preventDefault();
        copyToModalClipboard();
        return;
      }
      if (key === 'x') {
        event.preventDefault();
        liftSelection();
        return;
      }
      if (key === 'v') {
        event.preventDefault();
        pasteClipboardAsFloat();
        return;
      }
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        handleRedo();
      }
    };

    const onKeyUp = (event) => {
      if (event.code === 'Space') {
        spaceHeldRef.current = false;
        setSpaceHeld(false);
        panDragRef.current = null;
      }
      if (event.key === 'z' || event.key === 'Z') {
        zHeldRef.current = false;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      spaceHeldRef.current = false;
      zHeldRef.current = false;
    };
  }, [
    open,
    busy,
    imageReady,
    saveDialogOpen,
    handleUndo,
    handleRedo,
    commitFloat,
    discardFloat,
    clearSelection,
    closePolygonalSelection,
    clearPathDraft,
    syncPathDraft,
    cropDraft,
    copyToModalClipboard,
    liftSelection,
    pasteClipboardAsFloat,
    tool,
    selectMode,
    pushHistory,
    presentDoc,
  ]);

  // Ctrl+scroll and Z+scroll zoom over the viewport (not when typing).
  useEffect(() => {
    if (!open) return undefined;
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const onWheel = (event) => {
      const zZoom = zHeldRef.current;
      const ctrlZoom = event.ctrlKey || event.metaKey;
      if (!zZoom && !ctrlZoom) return;
      event.preventDefault();
      const direction = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom((prev) => clampZoom(prev + direction));
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [open, imageReady]);

  const handleResetAll = () => {
    discardFloat();
    const snap = pristineSnapRef.current;
    const doc = docRef.current;
    if (!snap || !doc) return;
    restoreEditorDocumentFromSnapshot(doc, snap);
    historyRef.current = createEditorDocumentHistory();
    pushEditorDocumentHistory(historyRef.current, doc);
    historyRef.current.index = 0;
    clearCowUniqueLayerIds(doc);
    presentDoc();
    setSampledColor(null);
    clearSelection();
    setCropDraft(null);
    syncHistoryUi();
  };

  const handleApplyRemoveBg = () => {
    if (floatObjRef.current) commitFloat();
    if (!sampledColor) return;
    const working = readWorking();
    if (!working) return;
    const data = working.data;
    const target = { r: sampledColor.r, g: sampledColor.g, b: sampledColor.b };
    for (let i = 0; i < data.length; i += 4) {
      const px = { r: data[i], g: data[i + 1], b: data[i + 2] };
      if (colorDistance(px, target) <= tolerance) {
        data[i + 3] = 0;
      }
    }
    writeWorking(working);
    pushHistory();
    setToolError('');
  };

  /**
   * @param {'h'|'v'} axis
   */
  const handleFlip = (axis) => {
    if (floatObjRef.current) commitFloat();
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    try {
      const src = ctx.getImageData(0, 0, w, h);
      const out = ctx.createImageData(w, h);
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const sx = axis === 'h' ? w - 1 - x : x;
          const sy = axis === 'v' ? h - 1 - y : y;
          const si = (sy * w + sx) * 4;
          const di = (y * w + x) * 4;
          out.data[di] = src.data[si];
          out.data[di + 1] = src.data[si + 1];
          out.data[di + 2] = src.data[si + 2];
          out.data[di + 3] = src.data[si + 3];
        }
      }
      writeWorking(out);
      pushHistory();
      setToolError('');
    } catch (err) {
      console.error('ImageEditorModal: flip failed', err);
      setToolError(PIXEL_READ_ERROR);
    }
  };

  /**
   * @param {number} x
   * @param {number} y
   */
  const paintAt = (x, y) => {
    const doc = docRef.current;
    const active = getActiveEditorLayer(doc);
    if (!doc || !active) return;
    if (isEditorTextLayer(active)) {
      paintingRef.current = false;
      lastPaintPtRef.current = null;
      layerPaintSeededRef.current = false;
      strokeDirtyRef.current = false;
      promptRasterizeActiveTextLayer();
      return;
    }

    if (!layerPaintSeededRef.current) {
      ensureLayerPixelsUnique(doc, active.id);
      const layer = getActiveEditorLayer(doc);
      let paintCanvas = layerPaintCanvasRef.current;
      if (!paintCanvas) {
        paintCanvas = document.createElement('canvas');
        layerPaintCanvasRef.current = paintCanvas;
      }
      if (
        paintCanvas.width !== doc.width ||
        paintCanvas.height !== doc.height
      ) {
        paintCanvas.width = doc.width;
        paintCanvas.height = doc.height;
      }
      const seedCtx = paintCanvas.getContext('2d', { willReadFrequently: true });
      if (!seedCtx || !layer) return;
      seedCtx.putImageData(layer.pixels, 0, 0);
      layerPaintSeededRef.current = true;
    }

    const paintCanvas = layerPaintCanvasRef.current;
    const ctx = paintCanvas?.getContext('2d', { willReadFrequently: true });
    const layer = getActiveEditorLayer(doc);
    if (!ctx || !layer) return;

    const rgb = hexToRgb(foregroundColor);
    ctx.save();
    ctx.lineCap = pencilMode ? 'butt' : 'round';
    ctx.lineJoin = pencilMode ? 'bevel' : 'round';
    ctx.lineWidth = Math.max(1, brushSize);
    if (eraser) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.fillStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      const css = `rgb(${rgb.r},${rgb.g},${rgb.b})`;
      ctx.strokeStyle = css;
      ctx.fillStyle = css;
    }
    const prev = lastPaintPtRef.current;
    if (pencilMode) {
      const size = Math.max(1, Math.round(brushSize));
      const stamp = (px, py) =>
        ctx.fillRect(
          Math.round(px) - Math.floor(size / 2),
          Math.round(py) - Math.floor(size / 2),
          size,
          size,
        );
      if (prev) {
        const dx = x - prev.x;
        const dy = y - prev.y;
        const steps = Math.max(Math.abs(dx), Math.abs(dy), 1);
        for (let i = 0; i <= steps; i += 1) {
          stamp(prev.x + (dx * i) / steps, prev.y + (dy * i) / steps);
        }
      } else {
        stamp(x, y);
      }
    } else if (prev) {
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(x, y, Math.max(0.5, brushSize / 2), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    try {
      const painted = ctx.getImageData(0, 0, doc.width, doc.height);
      layer.pixels.data.set(painted.data);
      doc.compositeDirty = true;
      presentDoc();
    } catch (err) {
      console.error('ImageEditorModal: paint sync failed', err);
      setToolError(PIXEL_READ_ERROR);
    }

    lastPaintPtRef.current = { x, y };
    strokeDirtyRef.current = true;
  };

  /**
   * Paint/erase one dab (with interpolation) into the active selection mask.
   * @param {number} x
   * @param {number} y
   */
  const selectBrushAt = (x, y) => {
    const sel = selectionRef.current;
    if (!sel?.mask) return;
    const erase = selectBrushEraseRef.current;
    const prev = lastPaintPtRef.current;
    let changed = false;
    const dab = (px, py) => {
      if (paintSelectionBrushDab(sel, px, py, brushSize, erase)) changed = true;
    };
    if (prev) {
      const dx = x - prev.x;
      const dy = y - prev.y;
      const dist = Math.hypot(dx, dy);
      const step = Math.max(1, brushSize / 4);
      const steps = Math.max(1, Math.ceil(dist / step));
      for (let i = 1; i <= steps; i += 1) {
        dab(prev.x + (dx * i) / steps, prev.y + (dy * i) / steps);
      }
    } else {
      dab(x, y);
    }
    lastPaintPtRef.current = { x, y };
    if (changed) {
      // New wrapper so React re-renders overlay; mask buffer is mutated in place.
      const next = { width: sel.width, height: sel.height, mask: sel.mask };
      selectionRef.current = next;
      setSelection(next);
    }
  };

  /**
   * Apply one dab of a Tier-3 retouch brush onto strokeWorkingRef.
   * @param {number} x
   * @param {number} y
   */
  const retouchAt = (x, y) => {
    const working = strokeWorkingRef.current;
    const source = strokeSourceRef.current;
    if (!working) return;
    const prev = lastPaintPtRef.current;
    let dirty = false;
    if (tool === TOOL_CLONE) {
      const offset = cloneOffsetRef.current;
      if (!offset || !source) return;
      dirty = applyCloneStampDab(
        working,
        source,
        x,
        y,
        offset,
        brushSize,
      );
    } else if (tool === TOOL_BLUR && source) {
      dirty = applyBlurBrushDab(
        working,
        source,
        x,
        y,
        brushSize,
        retouchStrength,
      );
    } else if (tool === TOOL_SHARPEN && source) {
      dirty = applySharpenBrushDab(
        working,
        source,
        x,
        y,
        brushSize,
        retouchStrength,
      );
    } else if (tool === TOOL_SMUDGE && source) {
      dirty = applySmudgeBrushDab(
        working,
        source,
        x,
        y,
        prev ? prev.x : null,
        prev ? prev.y : null,
        brushSize,
        retouchStrength,
      );
    } else if (tool === TOOL_DODGE) {
      dirty = applyDodgeBurnBrushDab(
        working,
        x,
        y,
        brushSize,
        retouchStrength,
        'dodge',
      );
    } else if (tool === TOOL_BURN) {
      dirty = applyDodgeBurnBrushDab(
        working,
        x,
        y,
        brushSize,
        retouchStrength,
        'burn',
      );
    } else if (tool === TOOL_SPONGE) {
      dirty = applySpongeBrushDab(
        working,
        x,
        y,
        brushSize,
        retouchStrength,
        spongeMode,
      );
    }
    if (dirty) {
      writeWorking(working);
      strokeDirtyRef.current = true;
    }
    lastPaintPtRef.current = { x, y };
  };

  const sampleAt = (x, y) => {
    const doc = docRef.current;
    if (!doc) return;
    try {
      const composite = compositeEditorDocument(doc);
      const rgb = sampleImageDataPixel(composite, x, y);
      if (!rgb) return;
      setSampledColor(rgb);
      setForegroundColor(rgbToHex(rgb));
      setActiveColorSlot('fg');
      setToolError('');
      setEyeDropper(false);
    } catch (err) {
      console.error('ImageEditorModal: eye-dropper failed', err);
      setToolError(PIXEL_READ_ERROR);
    }
  };

  const handleCanvasPointerDown = (event) => {
    if (!imageReady || busy) return;

    const rawPt = clientToCanvas(event.clientX, event.clientY);
    if (rawPt) lastCanvasPtRef.current = rawPt;

    if (panMode || spaceHeldRef.current) {
      const viewport = viewportRef.current;
      if (!viewport) return;
      panDragRef.current = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: viewport.scrollLeft,
        scrollTop: viewport.scrollTop,
      };
      setIsPanning(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
      return;
    }

    if (floatObjRef.current) {
      const float = floatObjRef.current;
      const hitPt = clientToCanvas(event.clientX, event.clientY);
      if (!hitPt) return;
      // Canvas clicks only (options-bar / other chrome never reach here).
      // Inside float or on a handle → transform; outside bounds → commit.
      // Exception: text float + options field focused → always commit so a
      // canvas click after typing does not require an intermediate blur.
      const hit = hitTestFloatHandle(float, hitPt.x, hitPt.y);
      if (
        shouldCommitFloatOnPointer(float, {
          hit,
          textOptionsFocused: isTextFloatOptionsFocused(),
        })
      ) {
        commitFloatFromPointer();
        return;
      }
      beginFloatDrag(hit, event);
      return;
    }

    const sampling = tool === TOOL_REMOVE_BG || eyeDropper;
    const pt = clientToToolPoint(event.clientX, event.clientY, {
      applySnap:
        (tool === TOOL_PAINT || isRetouchBrushTool(tool) || tool === TOOL_GRADIENT) &&
        !sampling,
    });
    if (!pt) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);

    if (sampling) {
      sampleAt(pt.x, pt.y);
      return;
    }

    if (tool === TOOL_CLONE && (event.altKey || cloneSetSourceMode)) {
      setCloneSource({ x: pt.x, y: pt.y });
      setCloneSetSourceMode(false);
      setToolError('');
      return;
    }

    if (tool === TOOL_GRADIENT) {
      gradientDragRef.current = { x0: pt.x, y0: pt.y };
      setGradientDraft({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
      return;
    }

    if (tool === TOOL_FILL) {
      const working = readWorking();
      if (!working) return;
      if (
        floodFillImageData(
          working,
          pt.x,
          pt.y,
          { ...hexToRgb(foregroundColor), a: 255 },
          selectTolerance,
          colorDistance,
        )
      ) {
        writeWorking(working);
        pushHistory();
      }
      return;
    }

    if (tool === TOOL_SAMPLER) {
      const doc = docRef.current;
      if (!doc) return;
      try {
        const composite = compositeEditorDocument(doc);
        const rgb = sampleImageDataPixel(composite, pt.x, pt.y);
        if (!rgb) return;
        const sample = { x: pt.x, y: pt.y, r: rgb.r, g: rgb.g, b: rgb.b };
        setColorSamples((prev) => [...prev.filter((p) => p.x !== pt.x || p.y !== pt.y), sample].slice(-MAX_COLOR_SAMPLES));
      } catch (err) {
        setToolError(PIXEL_READ_ERROR);
      }
      return;
    }

    if (tool === TOOL_TEXT) {
      // Place an empty float immediately in edit mode — type on canvas (Slice A).
      // Do not require the legacy options-bar Content field first.
      const textParams = {
        text: '',
        fontFamily: textFontFamily,
        fontSize: textFontSize,
        color: foregroundColor,
        align: textAlign,
        bold: textBold,
        italic: textItalic,
        runs: [
          {
            text: '',
            fontFamily: textFontFamily,
            fontSize: textFontSize,
            color: foregroundColor,
            bold: textBold,
            italic: textItalic,
          },
        ],
      };
      const imageData = renderTextToImageData(textParams);
      if (!imageData) {
        setToolError('Could not create a text float in this browser.');
        return;
      }
      replaceFloat(
        createFloat({
          imageData,
          cx: pt.x,
          cy: pt.y,
          origin: 'text',
          labelHint: textLayerPreviewLabel(''),
          textParams,
        }),
      );
      setTextToolContent('');
      setTool(TOOL_MOVE);
      setToolError('');
      // Same React tick as setFloatObj → no handle flash before edit mode.
      enterTextFloatEditMode();
      return;
    }

    if (tool === TOOL_PEN) {
      let path = pathRef.current;
      if (path.closed) {
        pathRef.current = createEmptyEditorPath();
        path = pathRef.current;
      }
      if (pathNearFirstAnchor(path, pt.x, pt.y)) {
        const closed = closeEditorPath(path);
        if (closed) {
          pathRef.current = closed;
          penDragRef.current = false;
          syncPathDraft();
        }
        return;
      }
      pathRef.current = appendPathAnchor(path, pt.x, pt.y);
      penDragRef.current = true;
      syncPathDraft({ hover: null });
      return;
    }

    if (isSelectionBuildingTool(tool)) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (selectMode === SELECT_WAND) {
        const working = readWorking();
        if (working) {
          const next = magicWandSelection(
            working,
            pt.x,
            pt.y,
            selectTolerance,
            colorDistance,
          );
          selectionRef.current = next;
          setSelection(next);
        }
        return;
      }
      if (selectMode === SELECT_BRUSH) {
        const next = ensureSelectionSize(
          selectionRef.current,
          canvas.width,
          canvas.height,
        );
        selectionRef.current = next;
        setSelection(next);
        selectBrushEraseRef.current = Boolean(event.altKey || selectEraseMode);
        selectBrushStrokeRef.current = true;
        paintingRef.current = true;
        lastPaintPtRef.current = null;
        selectBrushAt(pt.x, pt.y);
        return;
      }
      if (selectMode === SELECT_POLY) {
        // Double-click closes (PointerEvent.detail); do not add another vertex.
        if (event.detail >= 2 && polyActiveRef.current) {
          if (lassoPointsRef.current.length >= 3) {
            closePolygonalSelection();
          }
          return;
        }
        if (!polyActiveRef.current) {
          polyActiveRef.current = true;
          lassoPointsRef.current = [{ x: pt.x, y: pt.y }];
        } else {
          const first = lassoPointsRef.current[0];
          const nearStart =
            first &&
            lassoPointsRef.current.length >= 3 &&
            Math.hypot(pt.x - first.x, pt.y - first.y) <= POLY_CLOSE_SNAP_PX;
          if (nearStart) {
            closePolygonalSelection();
            return;
          }
          lassoPointsRef.current = [
            ...lassoPointsRef.current,
            { x: pt.x, y: pt.y },
          ];
        }
        setSelectDraft({
          type: 'poly',
          points: lassoPointsRef.current,
          hover: { x: pt.x, y: pt.y },
          closed: false,
        });
        return;
      }
      if (selectMode === SELECT_MAGNETIC) {
        const working = readWorking();
        if (!working) return;
        magneticMagRef.current = sobelMagnitude(working);
        const snapped = snapPointToEdge(
          magneticMagRef.current,
          canvas.width,
          canvas.height,
          pt.x,
          pt.y,
          magneticRadius,
        );
        selectDragRef.current = { x0: snapped.x, y0: snapped.y };
        lassoPointsRef.current = [snapped];
        setSelectDraft({
          type: 'magnetic',
          points: lassoPointsRef.current,
          closed: false,
        });
        return;
      }
      selectDragRef.current = { x0: pt.x, y0: pt.y };
      lassoPointsRef.current = [{ x: pt.x, y: pt.y }];
      setSelectDraft(
        selectMode === SELECT_LASSO
          ? { type: 'lasso', points: lassoPointsRef.current }
          : { type: selectMode, x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y },
      );
      return;
    }

    if (tool === TOOL_CROP) {
      cropDragRef.current = { x0: pt.x, y0: pt.y };
      setCropDraft({ type: 'crop', x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
      return;
    }

    if (tool === TOOL_PAINT) {
      const active = getActiveEditorLayer(docRef.current);
      if (active && isEditorTextLayer(active)) {
        promptRasterizeActiveTextLayer();
        return;
      }
      paintingRef.current = true;
      strokeDirtyRef.current = false;
      lastPaintPtRef.current = null;
      paintAt(pt.x, pt.y);
      return;
    }

    if (isRetouchBrushTool(tool)) {
      if (tool === TOOL_CLONE && !cloneSource) {
        setToolError(
          'Alt+click (or Set source) to pick a clone sample point first.',
        );
        return;
      }
      const working = readWorking();
      if (!working) return;
      const snapshot = cloneImageData(working);
      strokeSourceRef.current = snapshot;
      strokeWorkingRef.current = cloneImageData(working);
      if (tool === TOOL_CLONE && cloneSource) {
        cloneOffsetRef.current = {
          x: cloneSource.x - pt.x,
          y: cloneSource.y - pt.y,
        };
      } else {
        cloneOffsetRef.current = null;
      }
      paintingRef.current = true;
      strokeDirtyRef.current = false;
      lastPaintPtRef.current = null;
      retouchAt(pt.x, pt.y);
    }
  };

  const handleCanvasPointerMove = (event) => {
    if (panDragRef.current) {
      const viewport = viewportRef.current;
      const drag = panDragRef.current;
      if (viewport && drag) {
        viewport.scrollLeft = drag.scrollLeft - (event.clientX - drag.x);
        viewport.scrollTop = drag.scrollTop - (event.clientY - drag.y);
      }
      return;
    }
    if (floatDragRef.current) {
      moveFloatDrag(event);
      return;
    }
    const pt = clientToToolPoint(event.clientX, event.clientY, {
      applySnap:
        tool === TOOL_PAINT ||
        isRetouchBrushTool(tool) ||
        tool === TOOL_GRADIENT ||
        (isSelectionBuildingTool(tool) && selectMode === SELECT_BRUSH),
    });
    if (isSelectionBuildingTool(tool) && selectMode === SELECT_POLY && polyActiveRef.current && pt) {
      const first = lassoPointsRef.current[0];
      const canClose =
        first &&
        lassoPointsRef.current.length >= 3 &&
        Math.hypot(pt.x - first.x, pt.y - first.y) <= POLY_CLOSE_SNAP_PX;
      setSelectDraft((draft) =>
        draft && draft.type === 'poly'
          ? {
              ...draft,
              hover: canClose
                ? { x: first.x, y: first.y }
                : { x: pt.x, y: pt.y },
              snapClose: Boolean(canClose),
            }
          : draft,
      );
      return;
    }
    if (tool === TOOL_PEN && pathRef.current?.anchors?.length && !pathRef.current.closed && pt) {
      if (penDragRef.current) {
        pathRef.current = updateLastAnchorDragHandles(
          pathRef.current,
          pt.x,
          pt.y,
        );
        syncPathDraft({ hover: null });
      } else {
        syncPathDraft({ hover: pt });
      }
      return;
    }
    if (selectDragRef.current && isSelectionBuildingTool(tool) && pt) {
      if (selectMode === SELECT_LASSO) {
        lassoPointsRef.current = [...lassoPointsRef.current, pt];
        setSelectDraft({ type: 'lasso', points: lassoPointsRef.current });
      } else if (selectMode === SELECT_MAGNETIC) {
        const canvas = canvasRef.current;
        const mag = magneticMagRef.current;
        if (canvas && mag) {
          const snapped = snapPointToEdge(
            mag,
            canvas.width,
            canvas.height,
            pt.x,
            pt.y,
            magneticRadius,
          );
          const last = lassoPointsRef.current[lassoPointsRef.current.length - 1];
          if (
            !last ||
            Math.hypot(snapped.x - last.x, snapped.y - last.y) >= MIN_MAGNETIC_STEP_PX
          ) {
            lassoPointsRef.current = [...lassoPointsRef.current, snapped];
            setSelectDraft({
              type: 'magnetic',
              points: lassoPointsRef.current,
              closed: false,
            });
          }
        }
      } else {
        setSelectDraft((draft) => (draft ? { ...draft, x1: pt.x, y1: pt.y } : draft));
      }
      return;
    }
    if (cropDragRef.current && tool === TOOL_CROP && pt) {
      setCropDraft((draft) => (draft ? { ...draft, x1: pt.x, y1: pt.y } : draft));
      return;
    }
    if (gradientDragRef.current && tool === TOOL_GRADIENT && pt) {
      const start = gradientDragRef.current;
      setGradientDraft({
        x0: start.x0,
        y0: start.y0,
        x1: pt.x,
        y1: pt.y,
      });
      return;
    }
    if (!paintingRef.current) return;
    if (!pt) return;
    if (
      isSelectionBuildingTool(tool) &&
      selectMode === SELECT_BRUSH &&
      selectBrushStrokeRef.current
    ) {
      selectBrushAt(pt.x, pt.y);
      return;
    }
    if (tool === TOOL_PAINT) {
      paintAt(pt.x, pt.y);
      return;
    }
    if (isRetouchBrushTool(tool)) {
      retouchAt(pt.x, pt.y);
    }
  };

  const endStroke = (event) => {
    if (panDragRef.current) {
      panDragRef.current = null;
      setIsPanning(false);
    }
    if (floatDragRef.current) {
      floatDragRef.current = null;
    }
    if (penDragRef.current) {
      penDragRef.current = false;
      syncPathDraft();
    }
    if (selectDragRef.current) {
      const drag = selectDragRef.current;
      const canvas = canvasRef.current;
      const pt = event
        ? clientToToolPoint(event.clientX, event.clientY)
        : null;
      if (canvas && pt && selectMode !== SELECT_POLY) {
        let next = null;
        if (selectMode === SELECT_LASSO || selectMode === SELECT_MAGNETIC) {
          next = lassoSelectionMask(
            canvas.width,
            canvas.height,
            lassoPointsRef.current,
          );
        } else if (selectMode === SELECT_ELLIPSE) {
          next = ellipseSelectionMask(
            canvas.width,
            canvas.height,
            drag.x0,
            drag.y0,
            pt.x,
            pt.y,
          );
        } else {
          next = rectSelectionMask(
            canvas.width,
            canvas.height,
            drag.x0,
            drag.y0,
            pt.x,
            pt.y,
          );
        }
        selectionRef.current = next;
        setSelection(next);
      }
      selectDragRef.current = null;
      lassoPointsRef.current = [];
      magneticMagRef.current = null;
      setSelectDraft(null);
    }
    if (cropDragRef.current) {
      cropDragRef.current = null;
    }
    if (gradientDragRef.current && tool === TOOL_GRADIENT) {
      const start = gradientDragRef.current;
      const pt = event
        ? clientToToolPoint(event.clientX, event.clientY)
        : null;
      gradientDragRef.current = null;
      const end = pt || (gradientDraft ? { x: gradientDraft.x1, y: gradientDraft.y1 } : null);
      setGradientDraft(null);
      if (start && end) {
        const working = readWorking();
        if (
          working &&
          applyLinearGradient(
            working,
            start.x0,
            start.y0,
            end.x,
            end.y,
            hexToRgb(foregroundColor),
            hexToRgb(backgroundColor),
            selectionHasPixels(selectionRef.current)
              ? selectionRef.current
              : null,
          )
        ) {
          writeWorking(working);
          pushHistory();
        }
      }
    }
    if (!paintingRef.current) return;
    paintingRef.current = false;
    selectBrushStrokeRef.current = false;
    selectBrushEraseRef.current = false;
    lastPaintPtRef.current = null;
    strokeSourceRef.current = null;
    strokeWorkingRef.current = null;
    cloneOffsetRef.current = null;
    layerPaintSeededRef.current = false;
    if (strokeDirtyRef.current) {
      pushHistory();
      strokeDirtyRef.current = false;
    }
  };

  const applyCrop = useCallback(() => {
    const draft = cropDraft;
    if (!draft || Math.abs(draft.x1 - draft.x0) < 1 || Math.abs(draft.y1 - draft.y0) < 1) {
      return;
    }
    // Auto-commit uncommitted float first (same rule as tool-switch).
    if (floatObjRef.current) commitFloat();
    const doc = docRef.current;
    if (!doc) return;
    const ok = applyEditorDocumentGeometry(doc, (pixels) =>
      cropImageData(pixels, draft.x0, draft.y0, draft.x1, draft.y1),
    );
    if (!ok) return;
    presentDoc();
    pushHistory();
    setVGuides([]);
    setHGuides([]);
    clearSelection();
    setCropDraft(null);
    setToolError('');
  }, [clearSelection, commitFloat, cropDraft, presentDoc, pushHistory]);

  /**
   * @param {string} hit
   * @param {React.PointerEvent} event
   */
  const beginFloatDrag = (hit, event) => {
    const current = floatObjRef.current;
    if (!current || busy) return;
    // Drop options-bar focus so Content selection chrome doesn't stick during drag.
    blurActiveTypingTarget();
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    floatDragRef.current = {
      mode: hit,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startFloat: { ...current, imageData: current.imageData },
    };
  };

  /**
   * True when a text-float options field (Content / Font / Size) owns focus.
   * @returns {boolean}
   */
  const isTextFloatOptionsFocused = () => {
    const ae = document.activeElement;
    if (!(ae instanceof HTMLElement)) return false;
    return Boolean(ae.closest('[data-testid="image-editor-text-float-options"]'));
  };

  /** Blur options-bar / other typing targets so FocusTrap won't restore them. */
  const blurActiveTypingTarget = () => {
    const ae = document.activeElement;
    if (!(ae instanceof HTMLElement)) return;
    const tag = ae.tagName;
    if (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      ae.isContentEditable
    ) {
      ae.blur();
    }
  };

  /**
   * Commit float from a pointer gesture; clears typing focus first.
   * @returns {boolean}
   */
  const commitFloatFromPointer = () => {
    blurActiveTypingTarget();
    return commitFloat();
  };

  /**
   * Pointer on float chrome (overlay) — always drag when hitting chrome.
   * Commit is only for outside-float / letterbox paths.
   * @param {string} hit
   * @param {React.PointerEvent} event
   */
  const handleFloatOverlayPointerDown = (hit, event) => {
    const float = floatObjRef.current;
    if (!float || busy) return;
    if (
      shouldCommitFloatOnPointer(float, {
        hit,
        textOptionsFocused: isTextFloatOptionsFocused(),
      })
    ) {
      event.preventDefault();
      event.stopPropagation();
      commitFloatFromPointer();
      return;
    }
    beginFloatDrag(hit, event);
  };

  /**
   * Letterbox / empty viewport around the image: commit active float.
   * Canvas itself handles in-bounds clicks via handleCanvasPointerDown.
   * @param {React.PointerEvent} event
   */
  const handleViewportPointerDownCapture = (event) => {
    if (!imageReady || busy || !floatObjRef.current) return;
    if (event.target.closest?.('[data-float-chrome="true"]')) return;
    if (event.target.closest?.('[data-no-float-commit="true"]')) return;
    if (event.target.closest?.('[data-text-edit-surface="true"]')) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const onCanvas =
      event.clientX >= rect.left &&
      event.clientX < rect.right &&
      event.clientY >= rect.top &&
      event.clientY < rect.bottom;
    if (onCanvas) return;

    const float = floatObjRef.current;
    if (
      !shouldCommitFloatOnPointer(float, {
        hit: null,
        textOptionsFocused: isTextFloatOptionsFocused(),
      })
    ) {
      return;
    }
    event.preventDefault();
    commitFloatFromPointer();
  };

  /**
   * @param {React.PointerEvent} event
   */
  const moveFloatDrag = (event) => {
    const drag = floatDragRef.current;
    const canvas = canvasRef.current;
    if (!drag || !canvas) return;
    const pt = clientToCanvas(event.clientX, event.clientY);
    if (!pt) return;
    lastCanvasPtRef.current = pt;
    const start = drag.startFloat;
    let next = { ...start, imageData: start.imageData };

    if (drag.mode === 'move') {
      const startPt = clientToCanvas(drag.startClientX, drag.startClientY);
      if (!startPt) return;
      next = {
        ...next,
        cx: start.cx + (pt.x - startPt.x),
        cy: start.cy + (pt.y - startPt.y),
      };
      next = snapFloatTranslation(next, {
        snapEnabled,
        showGrid,
        gridSize,
        vGuides,
        hGuides,
        width: canvas.width,
        height: canvas.height,
        snapAxisValue,
      });
    } else if (drag.mode === 'rotate') {
      next = {
        ...next,
        rotationDeg: rotationDegFromPointer(start.cx, start.cy, pt.x, pt.y),
      };
    } else if (
      drag.mode === 'nw' ||
      drag.mode === 'ne' ||
      drag.mode === 'se' ||
      drag.mode === 'sw'
    ) {
      next = withUniformFloatScale(
        next,
        scaleFromCornerDrag(start, drag.mode, pt.x, pt.y),
      );
    } else if (
      drag.mode === 'n' ||
      drag.mode === 'e' ||
      drag.mode === 's' ||
      drag.mode === 'w'
    ) {
      next = floatFromEdgeDrag(start, drag.mode, pt.x, pt.y);
    }

    floatObjRef.current = next;
    setFloatObj(next);
  };

  const endFloatDrag = () => {
    floatDragRef.current = null;
  };

  const makeSourceAsset = (id) => ({
    id,
    manifestId: null,
    type: 'sprite',
    category: 'prop',
    prompt: '',
  });

  const handleNewDocument = () => {
    const width = clampImageEditorCanvasDimension(newWidth);
    const height = clampImageEditorCanvasDimension(newHeight);
    const preview = buildSaveFilePreview(newFilename, 'png');
    if (!preview.valid) {
      setToolError('Enter a filename before creating a new canvas.');
      return;
    }
    if (!bootstrapBlankCanvas(width, height)) {
      setLoadError('Could not create a blank canvas in this browser.');
      return;
    }
    const assetLabel = preview.fileStem;
    onDocumentChange?.({
      kind: 'blank',
      imageUrl: '',
      assetLabel,
      sourceAsset: makeSourceAsset(assetLabel),
      objectUrl: null,
    });
    setNewDialogOpen(false);
    setFileMenuAnchor(null);
  };

  const clampCanvasDimension = (value) =>
    Math.max(1, Math.min(MAX_CANVAS_DIMENSION, Math.round(Number(value) || 1)));

  const openImageSizeDialog = () => {
    setImageWidth(canvasSize.width || 1);
    setImageHeight(canvasSize.height || 1);
    setLockImageAspect(true);
    setImageSizeDialogOpen(true);
    setImageMenuAnchor(null);
  };

  const openCanvasSizeDialog = () => {
    setNewCanvasWidth(canvasSize.width || 1);
    setNewCanvasHeight(canvasSize.height || 1);
    setCanvasAnchor('center');
    setCanvasSizeDialogOpen(true);
    setImageMenuAnchor(null);
  };

  const updateImageDimension = (axis, value) => {
    const next = clampCanvasDimension(value);
    const sourceWidth = canvasSize.width || 1;
    const sourceHeight = canvasSize.height || 1;
    if (axis === 'width') {
      setImageWidth(next);
      if (lockImageAspect) {
        setImageHeight(clampCanvasDimension((next * sourceHeight) / sourceWidth));
      }
    } else {
      setImageHeight(next);
      if (lockImageAspect) {
        setImageWidth(clampCanvasDimension((next * sourceWidth) / sourceHeight));
      }
    }
  };

  const applyImageSize = () => {
    // Auto-commit uncommitted float first (same rule as tool-switch).
    if (floatObjRef.current) commitFloat();
    const doc = docRef.current;
    if (!doc) return;
    const width = clampCanvasDimension(imageWidth);
    const height = clampCanvasDimension(imageHeight);
    // Smooth is the sole default; nearest-neighbor can be added for pixel art later.
    const ok = applyEditorDocumentGeometry(doc, (pixels) =>
      scaleImageData(pixels, width, height, { smooth: true }),
    );
    if (!ok) {
      setToolError('Could not scale the image in this browser.');
      return;
    }
    presentDoc();
    pushHistory();
    clearSelection();
    setVGuides([]);
    setHGuides([]);
    setImageSizeDialogOpen(false);
    setToolError('');
  };

  const applyCanvasSize = () => {
    // Auto-commit uncommitted float first (same rule as tool-switch).
    if (floatObjRef.current) commitFloat();
    const doc = docRef.current;
    if (!doc) return;
    const width = clampCanvasDimension(newCanvasWidth);
    const height = clampCanvasDimension(newCanvasHeight);
    // Validate the resolved placement; same anchor applied to every layer.
    const anchorOffset = canvasAnchorOffset(
      doc.width,
      doc.height,
      width,
      height,
      canvasAnchor,
    );
    if (!Number.isFinite(anchorOffset.x) || !Number.isFinite(anchorOffset.y)) return;
    const ok = applyEditorDocumentGeometry(doc, (pixels) =>
      resizeCanvasImageData(pixels, width, height, canvasAnchor),
    );
    if (!ok) return;
    presentDoc();
    pushHistory();
    clearSelection();
    setVGuides([]);
    setHGuides([]);
    setCanvasSizeDialogOpen(false);
    setToolError('');
  };

  const readFileIntoImageData = (file, onReady) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      setToolError('Choose an image file (PNG, JPEG, WebP, etc.).');
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(
          1,
          MAX_CANVAS_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight, 1),
        );
        const temp = document.createElement('canvas');
        temp.width = Math.max(1, Math.round(img.naturalWidth * scale));
        temp.height = Math.max(1, Math.round(img.naturalHeight * scale));
        const ctx = temp.getContext('2d', { willReadFrequently: true });
        if (!ctx) throw new Error('No 2D canvas context');
        ctx.drawImage(img, 0, 0, temp.width, temp.height);
        onReady(ctx.getImageData(0, 0, temp.width, temp.height), objectUrl);
      } catch (err) {
        console.error('ImageEditorModal: local image read failed', err);
        URL.revokeObjectURL(objectUrl);
        setToolError(PIXEL_READ_ERROR);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setToolError('Could not load the selected image file.');
    };
    img.src = objectUrl;
  };

  const handleOpenLocalFile = (file) => {
    readFileIntoImageData(file, (imageData, objectUrl) => {
      resetDocumentChrome();
      if (!bootstrapCanvasFromImageData(imageData)) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      const base = (String(file.name || 'local').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60) || 'local');
      onDocumentChange?.({
        kind: 'local',
        imageUrl: objectUrl,
        assetLabel: base,
        sourceAsset: makeSourceAsset(base),
        objectUrl,
      });
      setFileMenuAnchor(null);
    });
  };

  const handleImportImage = (file) => {
    if (!imageReady) {
      setToolError('Open or create a canvas before importing an image.');
      return;
    }
    readFileIntoImageData(file, (imageData, objectUrl) => {
      const canvas = canvasRef.current;
      URL.revokeObjectURL(objectUrl);
      if (!canvas) return;
      const anchor = lastCanvasPtRef.current;
      // Fit inside the document so corner/edge/rotate handles stay on-canvas.
      // Pre-existing Import gap (not Slice 2): full-res imports at scale 1 put
      // handles outside the clipped viewport while Paste (typically smaller)
      // kept them visible.
      const scale = fitFloatScaleToDocument(
        imageData.width,
        imageData.height,
        canvas.width,
        canvas.height,
      );
      replaceFloat(
        createFloat({
          imageData,
          cx: anchor?.x ?? canvas.width / 2,
          cy: anchor?.y ?? canvas.height / 2,
          scale,
          origin: 'import',
        }),
      );
      setTool(TOOL_MOVE);
      setToolError('');
      setFileMenuAnchor(null);
    });
  };

  const openGenerateDialog = () => {
    setImageMenuAnchor(null);
    setGenerateError('');
    setGenerateSoftenMask(false);
    if (!imageReady) {
      setGenerateMode(GENERATE_MODE_TXT2IMG);
    }
    setGenerateDialogOpen(true);
  };

  /**
   * Init image for img2img: active float pixels, else working canvas PNG.
   * @returns {string|null} data URL
   */
  const captureInitImageDataUrl = () => {
    const float = floatObjRef.current;
    if (float && float.imageData) {
      const off = imageDataToCanvas(float.imageData);
      if (off) return off.toDataURL('image/png');
    }
    const canvas = canvasRef.current;
    if (!canvas || !canvas.width || !canvas.height) return null;
    try {
      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('ImageEditorModal: init image capture failed', err);
      return null;
    }
  };

  const landGeneratedAsFloat = async (imageDataUrl, origin = 'generate') => {
    const canvas = canvasRef.current;
    if (!canvas) {
      throw new Error('Canvas is not ready.');
    }
    const imageData = await loadImageSrcToImageData(imageDataUrl);
    const anchor = lastCanvasPtRef.current;
    replaceFloat(
      createFloat({
        imageData,
        cx: anchor?.x ?? canvas.width / 2,
        cy: anchor?.y ?? canvas.height / 2,
        origin:
          origin === 'img2img' || origin === 'inpaint' || origin === 'generate'
            ? origin
            : 'generate',
      }),
    );
    setTool(TOOL_MOVE);
  };

  const runGenerate = async (modeOverride) => {
    const activeMode =
      typeof modeOverride === 'string' ? modeOverride : generateMode;
    const prompt = (generatePrompt || '').trim();
    const resolvedCompanyId =
      typeof companyId === 'string' ? companyId.trim() : '';
    const resolvedProjectId =
      typeof projectId === 'string' ? projectId.trim() : '';

    if (!resolvedCompanyId) {
      setGenerateError(
        'Workspace company is required for AI generation (spend gating).',
      );
      return;
    }
    if (!prompt) {
      setGenerateError('Enter a prompt before generating.');
      return;
    }
    if (activeMode === GENERATE_MODE_IMG2IMG && !imageReady) {
      setGenerateError('Open or create a canvas before image-to-image.');
      return;
    }
    if (activeMode === GENERATE_MODE_INPAINT) {
      if (!imageReady) {
        setGenerateError('Open or create a canvas before inpainting.');
        return;
      }
      if (!selectionHasPixels(selectionRef.current || selection)) {
        setGenerateError('Make a selection first');
        return;
      }
    }

    let baseImage;
    if (
      activeMode === GENERATE_MODE_IMG2IMG ||
      activeMode === GENERATE_MODE_INPAINT
    ) {
      baseImage = captureInitImageDataUrl();
      if (!baseImage) {
        setGenerateError(
          activeMode === GENERATE_MODE_INPAINT
            ? 'Could not capture the current image for inpaint.'
            : 'Could not capture the current image for image-to-image.',
        );
        return;
      }
    }

    let maskImage;
    if (activeMode === GENERATE_MODE_INPAINT) {
      try {
        maskImage = await selectionMaskToStabilityPngDataUrl(
          selectionRef.current || selection,
          {
            soften: generateSoftenMask,
            softenRadiusPx: 2,
          },
        );
      } catch (maskErr) {
        setGenerateError(
          maskErr?.message || 'Make a selection first',
        );
        return;
      }
    }

    const aspectRatio = aspectRatioLabelFromSize(
      canvasSize.width || 1024,
      canvasSize.height || 1024,
    );

    setGenerateBusy(true);
    setGenerateError('');
    setToolError('');
    try {
      /** @type {Record<string, unknown>} */
      const payload = {
        mode: activeMode,
        prompt,
        companyId: resolvedCompanyId,
        aspectRatio,
      };
      if (resolvedProjectId) payload.projectId = resolvedProjectId;
      if (activeMode === GENERATE_MODE_IMG2IMG) {
        payload.baseImage = baseImage;
        payload.imageStrength = generateStrength;
      }
      if (activeMode === GENERATE_MODE_INPAINT) {
        payload.baseImage = baseImage;
        payload.maskImage = maskImage;
      }

      const result = await requestImageEditorAi(payload);
      const data = result?.data || {};
      const imageDataUrl =
        (typeof data.imageDataUrl === 'string' && data.imageDataUrl) ||
        (typeof data.imageBase64 === 'string' && data.imageBase64
          ? `data:image/png;base64,${data.imageBase64}`
          : '');
      if (!imageDataUrl) {
        throw new Error('Generation returned no image.');
      }
      await landGeneratedAsFloat(
        imageDataUrl,
        activeMode === GENERATE_MODE_IMG2IMG
          ? 'img2img'
          : activeMode === GENERATE_MODE_INPAINT
            ? 'inpaint'
            : 'generate',
      );
      setGenerateDialogOpen(false);
      setGeneratePrompt('');
    } catch (err) {
      console.error('ImageEditorModal: generate failed', err);
      const mapped = mapImageEditorAiError(err);
      setGenerateError(mapped);
      setToolError(mapped);
    } finally {
      setGenerateBusy(false);
    }
  };

  const openSaveDialog = () => {
    if (floatObjRef.current) commitFloat();
    if (!imageReady || !dirty || busy || saveInFlight) return;
    const doc = docRef.current;
    setSaveFilename(assetLabel || 'untitled');
    setSaveFormat(normalizeImageEditorFormat(preferredFormat));
    setSaveKind(doc && doc.layers.length > 1 ? 'project' : 'image');
    setJpegAlphaAck(false);
    setSaveHasAlpha(
      canvasRef.current ? canvasHasTransparency(canvasRef.current) : false,
    );
    setSaveDialogOpen(true);
  };

  /**
   * @param {'download'|'save'} mode
   */
  const exportFlatImage = async (mode) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!savePreview.valid) {
      setToolError('Enter a filename before saving.');
      return;
    }
    if (mode === 'save' && jpegNeedsAlphaWarning) {
      setToolError(
        'JPEG has no alpha channel. Confirm the transparency warning before saving.',
      );
      return;
    }
    if (mode === 'save' && saveInFlight) return;
    if (mode === 'save') setSaveInFlight(true);
    try {
      const blob = await exportCanvasBlob(canvas, {
        format: saveFormat,
        backgroundColor,
      });
      if (mode === 'download') {
        try {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = savePreview.fileName || `untitled.${saveFormatMeta.extension}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          setSaveDialogOpen(false);
        } catch (dlErr) {
          console.error('ImageEditorModal: download failed', dlErr);
          setToolError(`Could not download the ${saveFormatMeta.label}.`);
        }
        return;
      }
      if (typeof onSave !== 'function') return;
      await onSave(blob, {
        fileStem: savePreview.fileStem,
        format: saveFormat,
        width: canvas.width,
        height: canvas.height,
      });
      setSaveDialogOpen(false);
      setToolError('');
    } catch (err) {
      console.error('ImageEditorModal: export failed', err);
      setToolError(
        err?.message ||
          `Could not export the processed image as ${saveFormatMeta.label}.`,
      );
    } finally {
      if (mode === 'save') setSaveInFlight(false);
    }
  };

  const saveLayeredProjectFromDialog = async () => {
    const current = docRef.current;
    if (!current || typeof onSaveProject !== 'function') return;
    if (!savePreview.valid) {
      setToolError('Enter a filename before saving.');
      return;
    }
    if (saveInFlight) return;
    setSaveInFlight(true);
    try {
      await onSaveProject({
        document: current,
        fileStem: savePreview.fileStem,
        kind: IMAGE_EDITOR_PROJECT_KIND,
      });
      current.persistedRevision = current.revision;
      syncHistoryUi();
      setSaveDialogOpen(false);
      setToolError('');
    } catch (err) {
      console.error('ImageEditorModal: save project failed', err);
      setToolError(err?.message || 'Could not save layered project.');
    } finally {
      setSaveInFlight(false);
    }
  };

  const confirmSave = () => {
    if (saveKind === 'project') {
      void saveLayeredProjectFromDialog();
      return;
    }
    void exportFlatImage('save');
  };
  const confirmDownload = () => {
    void exportFlatImage('download');
  };

  /**
   * @param {'v'|'h'} axis
   * @param {number} clientPos client X for v, client Y for h
   */
  const addGuideFromRuler = (axis, clientPos) => {
    const canvas = canvasRef.current;
    if (!canvas || !imageReady) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    if (axis === 'v') {
      const x = Math.round(
        ((clientPos - rect.left) / rect.width) * canvas.width,
      );
      const clamped = Math.max(0, Math.min(canvas.width - 1, x));
      setVGuides((prev) => [...prev, clamped]);
    } else {
      const y = Math.round(
        ((clientPos - rect.top) / rect.height) * canvas.height,
      );
      const clamped = Math.max(0, Math.min(canvas.height - 1, y));
      setHGuides((prev) => [...prev, clamped]);
    }
  };

  /**
   * @param {'v'|'h'} axis
   * @param {number} index
   * @param {React.PointerEvent} event
   */
  const beginGuideDrag = (axis, index, event) => {
    event.preventDefault();
    event.stopPropagation();
    draggingGuideRef.current = { axis, index };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  /**
   * @param {React.PointerEvent} event
   */
  const moveGuideDrag = (event) => {
    const drag = draggingGuideRef.current;
    if (!drag) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    if (drag.axis === 'v') {
      const x = Math.round(
        ((event.clientX - rect.left) / rect.width) * canvas.width,
      );
      const clamped = Math.max(0, Math.min(canvas.width - 1, x));
      setVGuides((prev) =>
        prev.map((g, i) => (i === drag.index ? clamped : g)),
      );
    } else {
      const y = Math.round(
        ((event.clientY - rect.top) / rect.height) * canvas.height,
      );
      const clamped = Math.max(0, Math.min(canvas.height - 1, y));
      setHGuides((prev) =>
        prev.map((g, i) => (i === drag.index ? clamped : g)),
      );
    }
  };

  const endGuideDrag = () => {
    draggingGuideRef.current = null;
  };

  /**
   * @param {'v'|'h'} axis
   * @param {number} index
   */
  const removeGuide = (axis, index) => {
    if (axis === 'v') {
      setVGuides((prev) => prev.filter((_g, i) => i !== index));
    } else {
      setHGuides((prev) => prev.filter((_g, i) => i !== index));
    }
  };

  const beginModalResize = (edge, event) => {
    if (fullscreen) return;
    event.preventDefault();
    event.stopPropagation();
    modalResizeRef.current = {
      edge,
      startX: event.clientX,
      startY: event.clientY,
      startSize: modalSize,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveModalResize = (event) => {
    const drag = modalResizeRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    let w = drag.startSize.w;
    let h = drag.startSize.h;
    if (drag.edge.includes('e')) w += dx;
    if (drag.edge.includes('w')) w -= dx;
    if (drag.edge.includes('s')) h += dy;
    if (drag.edge.includes('n')) h -= dy;
    setModalSize({
      w: Math.max(MIN_MODAL_SIZE.w, Math.min(window.innerWidth, Math.round(w))),
      h: Math.max(MIN_MODAL_SIZE.h, Math.min(window.innerHeight, Math.round(h))),
    });
  };

  const endModalResize = () => {
    modalResizeRef.current = null;
  };

  const canvasCursor = panActive
    ? isPanning
      ? 'grabbing'
      : 'grab'
    : tool === TOOL_PAINT && !eyeDropper
      ? eraser
        ? 'cell'
        : 'crosshair'
      : 'crosshair';

  const displayWidth =
    canvasSize.width > 0 ? Math.round(canvasSize.width * zoom) : undefined;
  const displayHeight =
    canvasSize.height > 0 ? Math.round(canvasSize.height * zoom) : undefined;

  const gridBackgroundSize =
    showGrid && canvasSize.width > 0
      ? `${(gridSize / canvasSize.width) * 100}% ${(gridSize / canvasSize.height) * 100}%`
      : undefined;

  return (
    <Dialog
      open={Boolean(open)}
      onClose={busy || saveInFlight ? undefined : onClose}
      maxWidth={false}
      fullScreen={fullscreen && imageReady}
      PaperProps={{
        sx: !imageReady
          ? {
              width: 420,
              maxWidth: '92vw',
              m: 2,
            }
          : fullscreen
          ? { display: 'flex', flexDirection: 'column', height: '100%' }
          : {
              position: 'relative',
              width: modalSize.w,
              height: modalSize.h,
              maxWidth: '100vw',
              maxHeight: '100vh',
              m: 0,
              display: 'flex',
              flexDirection: 'column',
            },
      }}
      data-testid={imageReady ? 'image-editor-shell' : 'image-editor-loading'}
    >
      {!imageReady ? (
        <>
          <DialogTitle sx={{ py: 1.5 }}>
            Loading document…
          </DialogTitle>
          <DialogContent>
            <Stack
              spacing={2}
              alignItems="center"
              sx={{ py: 3, px: 1, textAlign: 'center' }}
            >
              {loadError ? (
                <>
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {loadError}
                  </Alert>
                  <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{ textTransform: 'none' }}
                  >
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <CircularProgress size={36} />
                  <Typography variant="body2" color="text.secondary">
                    Opening{assetLabel ? ` “${assetLabel}”` : ' document'}…
                  </Typography>
                  <LinearProgress
                    sx={{ width: '100%', maxWidth: 280 }}
                    data-testid="image-editor-load-progress"
                  />
                </>
              )}
            </Stack>
          </DialogContent>
        </>
      ) : (
        <>
      {!fullscreen
        ? ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'].map((edge) => (
            <Box
              key={edge}
              role="presentation"
              onPointerDown={(event) => beginModalResize(edge, event)}
              onPointerMove={moveModalResize}
              onPointerUp={endModalResize}
              onPointerCancel={endModalResize}
              sx={{
                position: 'absolute',
                zIndex: 20,
                ...(edge === 'n' || edge === 's'
                  ? { left: 10, right: 10, height: 8, [edge]: -4, cursor: 'ns-resize' }
                  : {}),
                ...(edge === 'e' || edge === 'w'
                  ? { top: 10, bottom: 10, width: 8, [edge]: -4, cursor: 'ew-resize' }
                  : {}),
                ...(edge.length === 2
                  ? {
                      width: 14,
                      height: 14,
                      [edge.includes('n') ? 'top' : 'bottom']: -6,
                      [edge.includes('w') ? 'left' : 'right']: -6,
                      cursor: `${edge}-resize`,
                    }
                  : {}),
              }}
            />
          ))
        : null}
      <DialogTitle
        sx={{ py: 1, pr: 6, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Typography variant="subtitle2" fontWeight={700} component="span">
          Edit image{assetLabel ? ` — ${assetLabel}` : ''}
        </Typography>
        <IconButton
          aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          onClick={() => setFullscreen((value) => !value)}
          size="small"
          disabled={busy}
          sx={{ position: 'absolute', right: 40, top: 8 }}
        >
          {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
        </IconButton>
        <IconButton
          aria-label="Close image editor"
          onClick={onClose}
          size="small"
          disabled={busy}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 0.5, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {loadError ? (
          <Alert severity="error" sx={{ mb: 0.5, py: 0 }}>
            {loadError}
          </Alert>
        ) : null}
        {toolError ? (
          <Alert
            severity="error"
            sx={{ mb: 0.5, py: 0 }}
            onClose={() => setToolError('')}
          >
            {toolError}
          </Alert>
        ) : null}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 0.25, minHeight: 28 }}
        >
          <Stack direction="row" spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Button size="small" onClick={(e) => setFileMenuAnchor(e.currentTarget)} disabled={busy || generateBusy || saveInFlight}>
              File
            </Button>
            <Button size="small" onClick={(e) => setEditMenuAnchor(e.currentTarget)} disabled={busy || generateBusy}>
              Edit
            </Button>
            <Button size="small" onClick={(e) => setImageMenuAnchor(e.currentTarget)} disabled={!imageReady || busy || generateBusy}>
              Image
            </Button>
            <Button size="small" onClick={(e) => setSelectMenuAnchor(e.currentTarget)} disabled={!imageReady || busy || generateBusy}>
              Select
            </Button>
          </Stack>
          {saveInFlight || autosaveStatus === 'saving' ? (
            <Box
              data-testid="image-editor-save-progress"
              sx={{
                width: '33%',
                maxWidth: 200,
                minWidth: 120,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.2, mb: 0.25 }}
              >
                {saveInFlight ? 'Saving…' : 'Autosaving…'}
              </Typography>
              <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
            </Box>
          ) : null}
          {autosaveStatus === 'error' && !toolError && !saveInFlight ? (
            <Tooltip title="Autosave failed. Your latest changes may not be stored yet.">
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.25}
                data-testid="image-editor-autosave-error"
                sx={{
                  width: '33%',
                  maxWidth: 240,
                  minWidth: 140,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ flex: 1, lineHeight: 1.2 }}
                >
                  Autosave failed
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Dismiss autosave error"
                  onClick={() => setAutosaveStatus('idle')}
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>
            </Tooltip>
          ) : null}
        </Stack>
        <Menu anchorEl={fileMenuAnchor} open={Boolean(fileMenuAnchor)} onClose={() => setFileMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              setNewWidth(512);
              setNewHeight(512);
              setNewFilename('untitled');
              setNewDialogOpen(true);
              setFileMenuAnchor(null);
            }}
          >
            <ListItemIcon><AddPhotoAlternateOutlinedIcon fontSize="small" /></ListItemIcon>New…
          </MenuItem>
          <MenuItem onClick={() => openInputRef.current?.click()}>
            <ListItemIcon><FileOpenOutlinedIcon fontSize="small" /></ListItemIcon>Open local image…
          </MenuItem>
          <MenuItem disabled={!imageReady} onClick={() => importInputRef.current?.click()}>
            <ListItemIcon><AddPhotoAlternateOutlinedIcon fontSize="small" /></ListItemIcon>Import image…
          </MenuItem>
          <Divider />
          <MenuItem
            disabled={!imageReady || !dirty || saveInFlight || busy}
            onClick={() => {
              openSaveDialog();
              setFileMenuAnchor(null);
            }}
            data-testid="file-menu-save"
          >
            Save
          </MenuItem>
          <MenuItem
            disabled={!imageReady || !dirty || saveInFlight || busy}
            onClick={() => {
              openSaveDialog();
              setFileMenuAnchor(null);
            }}
            data-testid="file-menu-save-as"
          >
            Save As…
          </MenuItem>
          <MenuItem
            disabled={!imageReady}
            onClick={() => {
              handleResetAll();
              setFileMenuAnchor(null);
            }}
            data-testid="file-menu-reset-all"
          >
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            Reset all
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setFileMenuAnchor(null);
              onClose();
            }}
            data-testid="file-menu-close"
          >
            Close
          </MenuItem>
        </Menu>
        <Menu anchorEl={editMenuAnchor} open={Boolean(editMenuAnchor)} onClose={() => setEditMenuAnchor(null)}>
          <MenuItem disabled={!canUndo} onClick={() => { handleUndo(); setEditMenuAnchor(null); }}>Undo</MenuItem>
          <MenuItem disabled={!canRedo} onClick={() => { handleRedo(); setEditMenuAnchor(null); }}>Redo</MenuItem>
          <Divider />
          <MenuItem disabled={!selectionHasPixels(selection)} onClick={() => { liftSelection(); setEditMenuAnchor(null); }}>
            <ListItemIcon><ContentCutOutlinedIcon fontSize="small" /></ListItemIcon>Cut
          </MenuItem>
          <MenuItem disabled={!imageReady} onClick={() => { copyToModalClipboard(); setEditMenuAnchor(null); }}>
            <ListItemIcon><ContentCopyOutlinedIcon fontSize="small" /></ListItemIcon>Copy
          </MenuItem>
          <MenuItem disabled={!clipboardReady} onClick={() => { pasteClipboardAsFloat(); setEditMenuAnchor(null); }}>Paste</MenuItem>
          <MenuItem disabled={!selectionHasPixels(selection)} onClick={() => { clearSelection(); setEditMenuAnchor(null); }}>Deselect</MenuItem>
        </Menu>
        <Menu anchorEl={imageMenuAnchor} open={Boolean(imageMenuAnchor)} onClose={() => setImageMenuAnchor(null)}>
          <MenuItem
            disabled={!imageReady || busy || generateBusy}
            onClick={openGenerateDialog}
          >
            <ListItemIcon><AutoAwesomeOutlinedIcon fontSize="small" /></ListItemIcon>
            Generate…
          </MenuItem>
          <Divider />
          <MenuItem onClick={openImageSizeDialog}>Image Size…</MenuItem>
          <MenuItem onClick={openCanvasSizeDialog}>Canvas Size…</MenuItem>
        </Menu>
        <Menu anchorEl={selectMenuAnchor} open={Boolean(selectMenuAnchor)} onClose={() => setSelectMenuAnchor(null)}>
          <MenuItem onClick={() => {
            const canvas = canvasRef.current;
            if (canvas) {
              const next = selectAllMask(canvas.width, canvas.height);
              selectionRef.current = next;
              setSelection(next);
            }
            setSelectMenuAnchor(null);
          }}>All</MenuItem>
          <MenuItem onClick={() => { clearSelection(); setSelectMenuAnchor(null); }}>Deselect</MenuItem>
          <MenuItem disabled={!selectionHasPixels(selection)} onClick={() => {
            const next = invertSelectionMask(selection);
            selectionRef.current = next;
            setSelection(next);
            setSelectMenuAnchor(null);
          }}>Invert</MenuItem>
        </Menu>
        <input ref={openInputRef} type="file" accept="image/*" hidden onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) handleOpenLocalFile(file); }} />
        <input ref={importInputRef} type="file" accept="image/*" hidden onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) handleImportImage(file); }} />

        <Paper
          variant="outlined"
          sx={{
            mb: 0.5,
            px: 1,
            py: 0.75,
            minHeight: 52,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            // Avoid overflow-x:auto alone — CSS forces overflow-y to auto too,
            // which creates a clip box that can hide the text caret in Chrome.
            overflow: 'visible',
            bgcolor: 'background.paper',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="nowrap"
            useFlexGap
            sx={{
              width: '100%',
              minHeight: 40,
              overflowX: 'auto',
              overflowY: 'visible',
              py: 0.25,
            }}
          >
            {floatObj ? (
              <>
                {floatObj.origin === 'text' ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    data-testid="image-editor-text-float-options"
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      Text
                    </Typography>
                    {textFloatEditMode ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        data-testid="text-float-edit-mode"
                        data-text-float-editing="true"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Editing on canvas · Enter newline · Ctrl/Cmd+Enter commits · Esc exits edit
                        (handles stay active — drag border to move while typing)
                      </Typography>
                    ) : (
                    <TextField
                      size="small"
                      label="Content"
                      value={
                        floatObj.textParams?.text ??
                        textToolContent
                      }
                      onChange={(e) => patchActiveTextFloat({ text: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        e.stopPropagation();
                        commitFloatFromPointer();
                      }}
                      disabled={busy}
                      inputRef={textContentInputRef}
                      sx={{
                        minWidth: 160,
                        '& .MuiInputBase-input': {
                          // Explicit caret — do NOT set transform on the input;
                          // translateZ(0) on <input> hides the caret in Chrome.
                          caretColor: (theme) => theme.palette.text.primary,
                        },
                      }}
                      inputProps={{
                        'data-testid': 'text-float-content',
                        spellCheck: true,
                      }}
                    />
                    )}
                    {!textFloatEditMode ? (
                      <Button
                        size="small"
                        onClick={() => enterTextFloatEditMode()}
                        disabled={busy}
                        data-testid="text-float-edit-on-canvas"
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Edit on canvas
                      </Button>
                    ) : null}
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel id="ie-text-float-font-label">Font</InputLabel>
                      <Select
                        labelId="ie-text-float-font-label"
                        label="Font"
                        data-testid="text-float-font"
                        value={
                          floatObj.textParams?.fontFamily || textFontFamily
                        }
                        onChange={(e) =>
                          patchActiveTextFloat({ fontFamily: e.target.value })
                        }
                        disabled={busy}
                      >
                        {IMAGE_EDITOR_TEXT_FONTS.map((font) => (
                          <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                            {font.split(',')[0].replace(/"/g, '')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Size"
                      type="number"
                      value={floatObj.textParams?.fontSize ?? textFontSize}
                      onChange={(e) =>
                        patchActiveTextFloat({
                          fontSize: Math.max(
                            8,
                            Math.min(256, Math.round(Number(e.target.value) || 32)),
                          ),
                        })
                      }
                      inputProps={{
                        min: 8,
                        max: 256,
                        step: 1,
                        'data-testid': 'text-float-size',
                      }}
                      sx={{
                        width: 80,
                        '& .MuiInputBase-input': {
                          caretColor: (theme) => theme.palette.text.primary,
                        },
                      }}
                      disabled={busy}
                    />
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={floatObj.textParams?.align || textAlign}
                      onChange={(_e, next) => next && patchActiveTextFloat({ align: next })}
                      disabled={busy}
                    >
                      <ToggleButton value="left" sx={{ textTransform: 'none', py: 0, px: 1 }}>
                        Left
                      </ToggleButton>
                      <ToggleButton value="center" sx={{ textTransform: 'none', py: 0, px: 1 }}>
                        Center
                      </ToggleButton>
                      <ToggleButton value="right" sx={{ textTransform: 'none', py: 0, px: 1 }}>
                        Right
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                      size="small"
                      value={[
                        ...((floatObj.textParams?.bold ?? textBold)
                          ? ['bold']
                          : []),
                        ...((floatObj.textParams?.italic ?? textItalic)
                          ? ['italic']
                          : []),
                      ]}
                      onChange={(_e, next) => {
                        const values = Array.isArray(next) ? next : [];
                        patchActiveTextFloat({
                          bold: values.includes('bold'),
                          italic: values.includes('italic'),
                        });
                      }}
                      disabled={busy}
                      data-testid="text-float-style"
                    >
                      <ToggleButton
                        value="bold"
                        aria-label="Bold"
                        sx={{ py: 0, px: 0.75 }}
                      >
                        <FormatBoldIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton
                        value="italic"
                        aria-label="Italic"
                        sx={{ py: 0, px: 0.75 }}
                      >
                        <FormatItalicIcon fontSize="small" />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Float
                  </Typography>
                )}
                <TextField
                  size="small"
                  label="Scale"
                  type="number"
                  value={Number(floatObj.scale.toFixed(2))}
                  onChange={(e) => {
                    const scale = clampFloatScale(Number(e.target.value));
                    const next = withUniformFloatScale(floatObj, scale);
                    floatObjRef.current = next;
                    setFloatObj(next);
                  }}
                  inputProps={{ step: 0.05, min: 0.05, max: 8 }}
                  sx={{ width: 88 }}
                  disabled={busy}
                />
                <TextField
                  size="small"
                  label="Rotate °"
                  type="number"
                  value={Math.round(floatObj.rotationDeg)}
                  onChange={(e) => {
                    const rotationDeg = normalizeRotationDeg(Number(e.target.value));
                    const next = { ...floatObj, rotationDeg };
                    floatObjRef.current = next;
                    setFloatObj(next);
                  }}
                  inputProps={{ step: 1 }}
                  sx={{ width: 96 }}
                  disabled={busy}
                />
                <Button
                  size="small"
                  onClick={() => commitFloat()}
                  disabled={!floatObj || busy}
                  sx={{ textTransform: 'none' }}
                >
                  Commit
                </Button>
                <Button
                  size="small"
                  onClick={() => discardFloat()}
                  disabled={!floatObj || busy}
                  sx={{ textTransform: 'none' }}
                >
                  Discard
                </Button>
              </>
            ) : null}
            {!floatObj && tool === TOOL_PAINT ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Brush: {brushSize}px
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={64}
                  value={brushSize}
                  onChange={(_e, value) =>
                    setBrushSize(Array.isArray(value) ? value[0] : value)
                  }
                  disabled={!imageReady || busy}
                  sx={{ width: 120 }}
                />
                <ToggleButton
                  size="small"
                  value="eraser"
                  selected={eraser}
                  onChange={() => {
                    setEraser((v) => !v);
                    setEyeDropper(false);
                  }}
                  disabled={!imageReady || busy}
                  sx={{ textTransform: 'none', px: 1, py: 0 }}
                >
                  Eraser
                </ToggleButton>
                <ToggleButton
                  size="small"
                  value="pencil"
                  selected={pencilMode}
                  onChange={() => setPencilMode((value) => !value)}
                  disabled={!imageReady || busy}
                  aria-label="Pencil (hard)"
                  sx={{ textTransform: 'none', px: 1, py: 0 }}
                >
                  Pencil
                </ToggleButton>
              </>
            ) : null}
            {!floatObj && isRetouchBrushTool(tool) ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Brush: {brushSize}px
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={64}
                  value={brushSize}
                  onChange={(_e, value) =>
                    setBrushSize(Array.isArray(value) ? value[0] : value)
                  }
                  disabled={!imageReady || busy}
                  sx={{ width: 120 }}
                />
                {tool === TOOL_CLONE ? (
                  <>
                    <ToggleButton
                      size="small"
                      value="set-source"
                      selected={cloneSetSourceMode}
                      onChange={() => setCloneSetSourceMode((v) => !v)}
                      disabled={!imageReady || busy}
                      sx={{ textTransform: 'none', px: 1, py: 0 }}
                    >
                      Set source
                    </ToggleButton>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {cloneSource
                        ? `Source: ${cloneSource.x}, ${cloneSource.y}`
                        : 'Alt+click to set source'}
                    </Typography>
                  </>
                ) : null}
                {tool !== TOOL_CLONE ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      Strength: {retouchStrength.toFixed(2)}
                    </Typography>
                    <Slider
                      size="small"
                      min={0.05}
                      max={1}
                      step={0.05}
                      value={retouchStrength}
                      onChange={(_e, value) =>
                        setRetouchStrength(
                          Array.isArray(value) ? value[0] : value,
                        )
                      }
                      disabled={!imageReady || busy}
                      sx={{ width: 100 }}
                    />
                  </>
                ) : null}
                {tool === TOOL_SPONGE ? (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={spongeMode}
                    onChange={(_e, next) => next && setSpongeMode(next)}
                    disabled={!imageReady || busy}
                  >
                    <ToggleButton value="saturate" sx={{ textTransform: 'none', py: 0 }}>
                      Saturate
                    </ToggleButton>
                    <ToggleButton value="desaturate" sx={{ textTransform: 'none', py: 0 }}>
                      Desaturate
                    </ToggleButton>
                  </ToggleButtonGroup>
                ) : null}
              </>
            ) : null}
            {!floatObj && tool === TOOL_REMOVE_BG ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Tolerance: {tolerance}
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={150}
                  value={tolerance}
                  onChange={(_e, value) =>
                    setTolerance(Array.isArray(value) ? value[0] : value)
                  }
                  disabled={!imageReady || busy}
                  sx={{ width: 140 }}
                />
              </>
            ) : null}
            {!floatObj && isSelectionBuildingTool(tool) ? (
              <>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={selectMode}
                  onChange={(_e, next) => {
                    if (next) setSelectMode(next);
                  }}
                  disabled={!imageReady || busy}
                >
                  {SELECT_MODE_OPTIONS.map((opt) => (
                    <ToggleButton
                      key={opt.value}
                      value={opt.value}
                      sx={{
                        textTransform: 'none',
                        px: 0.75,
                        py: 0,
                        fontSize: 11,
                        minWidth: 0,
                      }}
                    >
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {selectMode === SELECT_WAND ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      Tolerance: {selectTolerance}
                    </Typography>
                    <Slider
                      size="small"
                      min={0}
                      max={150}
                      value={selectTolerance}
                      onChange={(_e, value) =>
                        setSelectTolerance(Array.isArray(value) ? value[0] : value)
                      }
                      sx={{ width: 120 }}
                    />
                  </>
                ) : null}
                {selectMode === SELECT_BRUSH ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      Brush: {brushSize}px
                    </Typography>
                    <Slider
                      size="small"
                      min={1}
                      max={64}
                      value={brushSize}
                      onChange={(_e, value) =>
                        setBrushSize(Array.isArray(value) ? value[0] : value)
                      }
                      disabled={!imageReady || busy}
                      sx={{ width: 120 }}
                    />
                    <ToggleButton
                      size="small"
                      value="select-erase"
                      selected={selectEraseMode}
                      onChange={() => setSelectEraseMode((v) => !v)}
                      disabled={!imageReady || busy}
                      sx={{ textTransform: 'none', px: 1, py: 0 }}
                    >
                      Erase
                    </ToggleButton>
                  </>
                ) : null}
              </>
            ) : null}
            {!floatObj && tool === TOOL_FILL ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Fill tolerance: {selectTolerance}
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={150}
                  value={selectTolerance}
                  onChange={(_e, value) =>
                    setSelectTolerance(Array.isArray(value) ? value[0] : value)
                  }
                  disabled={!imageReady || busy}
                  sx={{ width: 140 }}
                />
              </>
            ) : null}
            {!floatObj && tool === TOOL_TEXT ? (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="text-tool-place-hint"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Click canvas to place, then type
                </Typography>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="ie-text-font-label">Font</InputLabel>
                  <Select
                    labelId="ie-text-font-label"
                    label="Font"
                    value={textFontFamily}
                    onChange={(e) => setTextFontFamily(e.target.value)}
                    disabled={!imageReady || busy}
                  >
                    {IMAGE_EDITOR_TEXT_FONTS.map((font) => (
                      <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                        {font.split(',')[0].replace(/"/g, '')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Size"
                  type="number"
                  value={textFontSize}
                  onChange={(e) =>
                    setTextFontSize(
                      Math.max(8, Math.min(256, Math.round(Number(e.target.value) || 32))),
                    )
                  }
                  inputProps={{ min: 8, max: 256, step: 1 }}
                  sx={{ width: 80 }}
                  disabled={!imageReady || busy}
                />
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={textAlign}
                  onChange={(_e, next) => next && setTextAlign(next)}
                  disabled={!imageReady || busy}
                >
                  <ToggleButton value="left" sx={{ textTransform: 'none', py: 0, px: 1 }}>
                    Left
                  </ToggleButton>
                  <ToggleButton value="center" sx={{ textTransform: 'none', py: 0, px: 1 }}>
                    Center
                  </ToggleButton>
                  <ToggleButton value="right" sx={{ textTransform: 'none', py: 0, px: 1 }}>
                    Right
                  </ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  size="small"
                  value={[
                    ...(textBold ? ['bold'] : []),
                    ...(textItalic ? ['italic'] : []),
                  ]}
                  onChange={(_e, next) => {
                    const values = Array.isArray(next) ? next : [];
                    setTextBold(values.includes('bold'));
                    setTextItalic(values.includes('italic'));
                  }}
                  disabled={!imageReady || busy}
                  data-testid="text-tool-style"
                >
                  <ToggleButton
                    value="bold"
                    aria-label="Bold"
                    sx={{ py: 0, px: 0.75 }}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton
                    value="italic"
                    aria-label="Italic"
                    sx={{ py: 0, px: 0.75 }}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </>
            ) : null}
            {!floatObj && tool === TOOL_PEN ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Click anchors · drag for curves · near start / Enter closes · Esc cancels
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const closed = closeEditorPath(pathRef.current);
                    if (closed) {
                      pathRef.current = closed;
                      syncPathDraft();
                    }
                  }}
                  disabled={
                    !imageReady ||
                    busy ||
                    Boolean(pathDraft?.path?.closed) ||
                    !(pathDraft?.path?.anchors?.length >= 3)
                  }
                  sx={{ textTransform: 'none' }}
                >
                  Close path
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => convertPathToSelection()}
                  disabled={!imageReady || busy || !pathDraft?.path?.closed}
                  sx={{ textTransform: 'none' }}
                >
                  Path → Selection
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => rasterizePathToLayer()}
                  disabled={!imageReady || busy || !pathDraft?.path?.closed}
                  sx={{ textTransform: 'none' }}
                >
                  Rasterize to layer
                </Button>
              </>
            ) : null}
            {!floatObj &&
            tool !== TOOL_PAINT &&
            !isRetouchBrushTool(tool) &&
            tool !== TOOL_REMOVE_BG &&
            !isSelectionBuildingTool(tool) &&
            tool !== TOOL_FILL &&
            tool !== TOOL_TEXT &&
            tool !== TOOL_PEN ? (
              <Typography variant="caption" color="text.secondary">
                {tool === TOOL_MOVE
                  ? 'Select pixels and Lift / Cut, or use Transform whole image.'
                  : tool === TOOL_CROP
                    ? 'Drag a crop region on the canvas.'
                    : tool === TOOL_SAMPLER
                      ? 'Click the canvas to sample colors.'
                      : tool === TOOL_GRADIENT
                        ? 'Drag on the canvas for a linear FG→BG gradient.'
                        : 'Tool options for the current tool.'}
              </Typography>
            ) : null}
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            gap: 0.75,
          }}
        >
          <Stack
            spacing={0.5}
            alignItems="center"
            sx={{
              flexShrink: 0,
              overflowY: 'auto',
              py: 0.25,
              pr: 0.5,
              borderRight: 1,
              borderColor: 'divider',
              maxWidth: 52,
            }}
          >
            <ToggleButtonGroup
            exclusive
            orientation="vertical"
            size="small"
            value={tool}
            onChange={(_e, next) => {
              if (next) {
                if (floatObjRef.current && next !== TOOL_MOVE) commitFloat();
                // Move tool = transform, not caret-typing: leave in-canvas edit
                // so full-body drag works (edge strips only apply while editing).
                if (next === TOOL_MOVE) exitTextFloatEditMode();
                if (next !== TOOL_PEN) clearPathDraft();
                setTool(next);
                setEyeDropper(false);
                setCloneSetSourceMode(false);
                if (next === TOOL_SELECT || next === TOOL_INPAINT) {
                  setPanelToolOpen(true);
                } else {
                  setSelectModeMenuAnchor(null);
                }
              }
            }}
            disabled={!imageReady || busy}
          >
            {[
              [TOOL_REMOVE_BG, 'Remove background', <AutoFixHighOutlinedIcon fontSize="small" />],
              [TOOL_PAINT, 'Paint', <BrushOutlinedIcon fontSize="small" />],
              [TOOL_SELECT, 'Select', <ContentCutOutlinedIcon fontSize="small" />],
              [TOOL_INPAINT, 'Inpaint', <AutoAwesomeOutlinedIcon fontSize="small" />],
              [TOOL_MOVE, 'Move', <OpenWithIcon fontSize="small" />],
              [TOOL_FILL, 'Fill', <FormatColorFillIcon fontSize="small" />],
              [TOOL_TEXT, 'Text', <TextFieldsIcon fontSize="small" />],
              [TOOL_PEN, 'Pen (path)', <CreateOutlinedIcon fontSize="small" />],
              [TOOL_CROP, 'Crop', <CropOutlinedIcon fontSize="small" />],
              [TOOL_SAMPLER, 'Sampler', <ColorizeOutlinedIcon fontSize="small" />],
            ].map(([value, title, icon]) => (
              <ToggleButton
                key={value}
                value={value}
                aria-label={title}
                title={title}
                data-testid={`image-editor-tool-${value}`}
                sx={{ px: 0.75, minWidth: 36 }}
                onClick={(event) => {
                  if (value === TOOL_SELECT || value === TOOL_INPAINT) {
                    setSelectModeMenuAnchor(event.currentTarget);
                    setPanelToolOpen(true);
                  }
                }}
              >
                {icon}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            exclusive
            orientation="vertical"
            size="small"
            value={
              [
                TOOL_CLONE,
                TOOL_BLUR,
                TOOL_SHARPEN,
                TOOL_SMUDGE,
                TOOL_DODGE,
                TOOL_BURN,
                TOOL_SPONGE,
                TOOL_GRADIENT,
              ].includes(tool)
                ? tool
                : null
            }
            onChange={(_e, next) => {
              if (next) {
                if (floatObjRef.current) commitFloat();
                clearPathDraft();
                setTool(next);
                setEyeDropper(false);
              }
            }}
            disabled={!imageReady || busy}
          >
            {[
              [TOOL_CLONE, 'Clone stamp', <ControlCameraIcon fontSize="small" />],
              [TOOL_BLUR, 'Blur', <BlurOnIcon fontSize="small" />],
              [TOOL_SHARPEN, 'Sharpen', <DetailsIcon fontSize="small" />],
              [TOOL_SMUDGE, 'Smudge', <GestureIcon fontSize="small" />],
              [TOOL_DODGE, 'Dodge', <BrightnessHighIcon fontSize="small" />],
              [TOOL_BURN, 'Burn', <BrightnessLowIcon fontSize="small" />],
              [TOOL_SPONGE, 'Sponge', <WaterDropOutlinedIcon fontSize="small" />],
              [TOOL_GRADIENT, 'Gradient', <GradientIcon fontSize="small" />],
            ].map(([value, title, icon]) => (
              <ToggleButton
                key={value}
                value={value}
                aria-label={title}
                title={title}
                sx={{ px: 0.75, minWidth: 36 }}
              >
                {icon}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
            <Tooltip title="Grab / pan (or hold Space)">
              <ToggleButton
                size="small"
                value="pan"
                selected={panMode}
                onChange={() => setPanMode((v) => !v)}
                disabled={!imageReady || busy}
                aria-label="Pan tool"
                sx={{ px: 0.75, minWidth: 36 }}
              >
                <PanToolOutlinedIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </Stack>
          <Popover
            open={Boolean(selectModeMenuAnchor) && isSelectionBuildingTool(tool)}
            anchorEl={selectModeMenuAnchor}
            onClose={() => setSelectModeMenuAnchor(null)}
            anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
            transformOrigin={{ vertical: 'center', horizontal: 'left' }}
            disableRestoreFocus
          >
            <Stack spacing={0.25} sx={{ p: 0.75, minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, pb: 0.25 }}>
                Selection mode
              </Typography>
              {SELECT_MODE_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  size="small"
                  variant={selectMode === opt.value ? 'contained' : 'text'}
                  onClick={() => {
                    setSelectMode(opt.value);
                    setSelectModeMenuAnchor(null);
                    setPanelToolOpen(true);
                  }}
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    px: 1,
                    minHeight: 28,
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Stack>
          </Popover>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <Box
          ref={viewportRef}
          data-testid="image-editor-viewport"
          data-transparency-bg={
            showTransparencyCheckerboard ? 'checkerboard' : 'solid'
          }
          onPointerDownCapture={handleViewportPointerDownCapture}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            bgcolor: showTransparencyCheckerboard ? 'grey.900' : '#6e6e6e',
            backgroundImage: showTransparencyCheckerboard
              ? 'linear-gradient(45deg, #444 25%, transparent 25%), linear-gradient(-45deg, #444 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #444 75%), linear-gradient(-45deg, transparent 75%, #444 75%)'
              : 'none',
            backgroundSize: showTransparencyCheckerboard ? '16px 16px' : undefined,
            backgroundPosition: showTransparencyCheckerboard
              ? '0 0, 0 8px, 8px -8px, -8px 0px'
              : undefined,
            borderRadius: 1,
            py: 2,
            px: 1,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            touchAction: 'none',
          }}
        >
          {!imageReady && !loadError ? <CircularProgress size={24} /> : null}
          <Box
            sx={{
              display: imageReady ? 'grid' : 'block',
              gridTemplateColumns: showRulers
                ? `${RULER_SIZE_PX}px max-content`
                : 'max-content',
              gridTemplateRows: showRulers
                ? `${RULER_SIZE_PX}px max-content`
                : 'max-content',
              maxWidth: '100%',
            }}
          >
            {imageReady && showRulers ? (
              <Box
                sx={{
                  width: RULER_SIZE_PX,
                  height: RULER_SIZE_PX,
                  bgcolor: 'grey.800',
                  borderRight: 1,
                  borderBottom: 1,
                  borderColor: 'grey.700',
                }}
              />
            ) : null}
            {imageReady && showRulers ? (
              <Box
                role="presentation"
                aria-label="Horizontal ruler — click to add a vertical guide"
                data-no-float-commit="true"
                onClick={(e) => addGuideFromRuler('v', e.clientX)}
                sx={{
                  height: RULER_SIZE_PX,
                  bgcolor: 'grey.800',
                  borderBottom: 1,
                  borderColor: 'grey.700',
                  cursor: 'crosshair',
                  backgroundImage:
                    'repeating-linear-gradient(90deg, transparent, transparent 9px, rgba255,255,255,0.2} 9px, rgba255,255,255,0.2} 10px)',
                }}
              />
            ) : null}
            {imageReady && showRulers ? (
              <Box
                role="presentation"
                aria-label="Vertical ruler — click to add a horizontal guide"
                data-no-float-commit="true"
                onClick={(e) => addGuideFromRuler('h', e.clientY)}
                sx={{
                  width: RULER_SIZE_PX,
                  bgcolor: 'grey.800',
                  borderRight: 1,
                  borderColor: 'grey.700',
                  cursor: 'crosshair',
                  backgroundImage:
                    'repeating-linear-gradient(180deg, transparent, transparent 9px, rgba255,255,255,0.2} 9px, rgba255,255,255,0.2} 10px)',
                }}
              />
            ) : null}
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                maxWidth: '100%',
                lineHeight: 0,
              }}
            >
              <Box
                component="canvas"
                ref={canvasRef}
                onPointerDown={handleCanvasPointerDown}
                onPointerMove={handleCanvasPointerMove}
                onPointerUp={endStroke}
                onPointerCancel={endStroke}
                onPointerLeave={endStroke}
                onDoubleClick={(event) => {
                  if (tool === TOOL_MOVE && !floatObjRef.current && imageReady) {
                    const pt = clientToCanvas(event.clientX, event.clientY);
                    const doc = docRef.current;
                    if (pt && doc) {
                      for (let i = doc.layers.length - 1; i >= 0; i -= 1) {
                        const layer = doc.layers[i];
                        if (!layer.visible) continue;
                        if (
                          isEditorTextLayer(layer) &&
                          hitTestEditorTextLayer(layer, pt.x, pt.y)
                        ) {
                          event.preventDefault();
                          beginTextLayerEdit(layer.id);
                          return;
                        }
                        if (
                          !isEditorTextLayer(layer) &&
                          hitTestRasterLayerPixel(layer, pt.x, pt.y)
                        ) {
                          event.preventDefault();
                          beginRasterLayerTransform(layer.id);
                          return;
                        }
                      }
                    }
                  }
                  if (
                    !isSelectionBuildingTool(tool) ||
                    selectMode !== SELECT_POLY ||
                    !polyActiveRef.current
                  ) {
                    return;
                  }
                  event.preventDefault();
                  if (lassoPointsRef.current.length >= 3) {
                    closePolygonalSelection();
                  }
                }}
                sx={{
                  display: imageReady ? 'block' : 'none',
                  width: displayWidth,
                  height: displayHeight,
                  maxWidth: 'none',
                  maxHeight: 'none',
                  cursor: canvasCursor,
                  imageRendering: 'pixelated',
                }}
              />
              {imageReady && showGrid ? (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    backgroundImage: `
                        linear-gradient(to right, rgba(0,229,255,0.35) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(0,229,255,0.35) 1px, transparent 1px)
                      `,
                    backgroundSize: gridBackgroundSize,
                  }}
                />
              ) : null}
              {imageReady
                ? vGuides.map((x, index) => (
                    <Box
                      key={`vg-${index}-${x}`}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Vertical guide at ${x}px — drag to move`}
                      title="Drag to move · double-click to remove"
                      onPointerDown={(e) => beginGuideDrag('v', index, e)}
                      onPointerMove={moveGuideDrag}
                      onPointerUp={endGuideDrag}
                      onPointerCancel={endGuideDrag}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeGuide('v', index);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${
                          canvasSize.width ? (x / canvasSize.width) * 100 : 0
                        }%`,
                        width: GUIDE_HIT_PAD_PX * 2,
                        ml: `-${GUIDE_HIT_PAD_PX}px`,
                        cursor: 'col-resize',
                        pointerEvents: 'auto',
                        zIndex: 2,
                        backgroundImage: `linear-gradient(to right, transparent ${
                          GUIDE_HIT_PAD_PX - 1
                        }px, ${'rgba(255,167,38,0.95)'} ${
                          GUIDE_HIT_PAD_PX - 1
                        }px, ${'rgba(255,167,38,0.95)'} ${
                          GUIDE_HIT_PAD_PX + 1
                        }px, transparent ${GUIDE_HIT_PAD_PX + 1}px)`,
                      }}
                    />
                  ))
                : null}
              {imageReady
                ? hGuides.map((y, index) => (
                    <Box
                      key={`hg-${index}-${y}`}
                      role="separator"
                      aria-orientation="horizontal"
                      aria-label={`Horizontal guide at ${y}px — drag to move`}
                      title="Drag to move · double-click to remove"
                      onPointerDown={(e) => beginGuideDrag('h', index, e)}
                      onPointerMove={moveGuideDrag}
                      onPointerUp={endGuideDrag}
                      onPointerCancel={endGuideDrag}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeGuide('h', index);
                      }}
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: `${
                          canvasSize.height ? (y / canvasSize.height) * 100 : 0
                        }%`,
                        height: GUIDE_HIT_PAD_PX * 2,
                        mt: `-${GUIDE_HIT_PAD_PX}px`,
                        cursor: 'row-resize',
                        pointerEvents: 'auto',
                        zIndex: 2,
                        backgroundImage: `linear-gradient(to bottom, transparent ${
                          GUIDE_HIT_PAD_PX - 1
                        }px, ${'rgba(255,167,38,0.95)'} ${
                          GUIDE_HIT_PAD_PX - 1
                        }px, ${'rgba(255,167,38,0.95)'} ${
                          GUIDE_HIT_PAD_PX + 1
                        }px, transparent ${GUIDE_HIT_PAD_PX + 1}px)`,
                      }}
                    />
                  ))
                : null}
              <ImageEditorSelectionOverlay
                selection={selection}
                draft={pathDraft || selectDraft || cropDraft}
                zoom={zoom}
                canvasWidth={canvasSize.width}
                canvasHeight={canvasSize.height}
              />
              {floatObj ? (
                <ImageEditorFloatOverlay
                  float={floatObj}
                  zoom={zoom}
                  busy={busy}
                  textEditMode={textFloatEditMode}
                  onPointerDownHandle={handleFloatOverlayPointerDown}
                  onPointerMoveHandle={moveFloatDrag}
                  onPointerUpHandle={endFloatDrag}
                  onEnterTextEdit={enterTextFloatEditMode}
                  onTextEditInput={(text) => patchActiveTextFloat({ text })}
                  onTextEditRuns={(runs) =>
                    patchActiveTextFloat({
                      runs,
                      text: flattenRunsToText(runs),
                    })
                  }
                  onTextEditCommit={() => commitFloatFromPointer()}
                  onTextEditExit={exitTextFloatEditMode}
                  onTextEditFlushReady={registerTextEditFlush}
                />
              ) : null}
              {tool === TOOL_CLONE && cloneSource && canvasSize.width > 0 ? (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    left: `${(cloneSource.x / canvasSize.width) * 100}%`,
                    top: `${(cloneSource.y / canvasSize.height) * 100}%`,
                    width: 14,
                    height: 14,
                    ml: '-7px',
                    mt: '-7px',
                    border: '2px solid',
                    borderColor: 'warning.main',
                    borderRadius: '50%',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.6)',
                    pointerEvents: 'none',
                    zIndex: 6,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: '50%',
                      top: -4,
                      bottom: -4,
                      width: 2,
                      ml: '-1px',
                      bgcolor: 'warning.main',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: -4,
                      right: -4,
                      height: 2,
                      mt: '-1px',
                      bgcolor: 'warning.main',
                    },
                  }}
                />
              ) : null}
              {gradientDraft && canvasSize.width > 0 ? (
                <Box
                  component="svg"
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                  viewBox={`0 0 ${canvasSize.width} ${canvasSize.height}`}
                  preserveAspectRatio="none"
                >
                  <line
                    x1={gradientDraft.x0}
                    y1={gradientDraft.y0}
                    x2={gradientDraft.x1}
                    y2={gradientDraft.y1}
                    stroke="#00e5ff"
                    strokeWidth={Math.max(1, 2 / Math.max(zoom, 0.25))}
                  />
                  <circle
                    cx={gradientDraft.x0}
                    cy={gradientDraft.y0}
                    r={Math.max(2, 4 / Math.max(zoom, 0.25))}
                    fill={foregroundColor}
                    stroke="#111"
                    strokeWidth={1}
                  />
                  <circle
                    cx={gradientDraft.x1}
                    cy={gradientDraft.y1}
                    r={Math.max(2, 4 / Math.max(zoom, 0.25))}
                    fill={backgroundColor}
                    stroke="#111"
                    strokeWidth={1}
                  />
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
            <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mt: 0.5,
            px: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 0,
          }}
        >
          {tool === TOOL_REMOVE_BG
            ? 'Click the image to sample a background color, adjust tolerance, then Apply. Works on the current canvas (compound with prior edits).'
            : tool === TOOL_INPAINT
              ? 'Build a mask with the selection modes, enter a prompt, then Run inpaint. Same spend path as Image → Generate…'
              : tool === TOOL_SELECT
              ? selectMode === SELECT_BRUSH
                ? 'Paint selection coverage with the brush. Toggle Erase or hold Alt to subtract from the mask.'
                : selectMode === SELECT_POLY
                  ? 'Click corners. Close by clicking the cyan start ring, double-click, Enter, or Close polygon.'
                  : selectMode === SELECT_MAGNETIC
                    ? 'Drag along contrast edges — cursor snaps locally (v1 Sobel radius, not full livewire).'
                    : 'Drag to select, or click with Wand. Selection modes are in the options bar above the canvas.'
              : tool === TOOL_MOVE
                ? floatObj
                  ? 'Drag the float to move it, then Commit or Discard.'
                  : 'Select pixels and Lift / Cut before moving them.'
                : tool === TOOL_FILL
                  ? 'Click a contiguous region to fill it with the foreground color.'
                  : tool === TOOL_TEXT
                    ? 'Set text options in the bar above, then click the canvas to place a text float.'
                  : tool === TOOL_CROP
                    ? 'Drag a crop region, then Apply crop. Cropping clears canvas-absolute guides.'
                    : tool === TOOL_SAMPLER
                      ? 'Click pixels to track up to four colors; click a sample chip to use it as the foreground color.'
                      : tool === TOOL_CLONE
                        ? 'Alt+click (or Set source) to pick a sample point, then paint — the source tracks the brush offset.'
                        : tool === TOOL_BLUR
                          ? 'Paint to blur pixels under the brush.'
                          : tool === TOOL_SHARPEN
                            ? 'Paint to sharpen edges under the brush.'
                            : tool === TOOL_SMUDGE
                              ? 'Drag to smear colors in the stroke direction.'
                              : tool === TOOL_DODGE
                                ? 'Paint to lighten pixels under the brush.'
                                : tool === TOOL_BURN
                                  ? 'Paint to darken pixels under the brush.'
                                  : tool === TOOL_SPONGE
                                    ? 'Paint to saturate or desaturate under the brush.'
                                    : tool === TOOL_GRADIENT
                                      ? 'Drag to paint a linear gradient from foreground to background color (respects active selection).'
            : eyeDropper
              ? 'Click the image to sample a foreground color.'
              : eraser
                ? 'Drag to erase (clears alpha).'
                : 'Drag to paint with the foreground color. Use eye-dropper to sample a color from the image.'}
          {showRulers
            ? ' Click the top ruler for a vertical guide, left ruler for horizontal. Drag guides to move; double-click a guide to remove.'
            : ''}
          {snapEnabled
            ? ' Snap is on for paint and float moves (overlay only — never baked into the PNG).'
            : ''}
          {' Ctrl+scroll or hold Z+scroll to zoom; Space+drag or Pan tool to move.'}
          {' Ctrl+C copies selection, float, or full canvas; Ctrl+X lifts the selection; Ctrl+V pastes as a float.'}
          {floatObj
            ? ' Transform whole image / paste creates one float — Commit (Enter) or Discard (Esc).'
            : ' Use Transform whole image to free-transform the buffer.'}
        </Typography>
          </Box>

          <Box
            sx={{
              width: optionsPanelWidth,
              flexShrink: 0,
              overflowY: 'auto',
              borderLeft: 1,
              borderColor: 'divider',
              pl: 0.75,
              pr: 0.25,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              minHeight: 0,
              position: 'relative',
            }}
          >
            <Box
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize options panel"
              onPointerDown={(event) => {
                event.preventDefault();
                event.currentTarget.setPointerCapture?.(event.pointerId);
                const startX = event.clientX;
                const startW = optionsPanelWidth;
                const target = event.currentTarget;
                const onMove = (ev) => {
                  const dx = startX - ev.clientX;
                  setOptionsPanelWidth(
                    Math.min(
                      OPTIONS_PANEL_WIDTH_MAX,
                      Math.max(OPTIONS_PANEL_WIDTH_MIN, Math.round(startW + dx)),
                    ),
                  );
                };
                const onUp = () => {
                  target.releasePointerCapture?.(event.pointerId);
                  target.removeEventListener('pointermove', onMove);
                  target.removeEventListener('pointerup', onUp);
                };
                target.addEventListener('pointermove', onMove);
                target.addEventListener('pointerup', onUp);
              }}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: 'ew-resize',
                zIndex: 2,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            />
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: panelPropsOpen ? 0.5 : 0, cursor: 'pointer' }}
                onClick={() => setPanelPropsOpen((v) => !v)}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Properties
                </Typography>
                <IconButton size="small" aria-label="Toggle Properties" sx={{ p: 0.25 }}>
                  {panelPropsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Stack>
              {panelPropsOpen ? (
                <>
              {imageReady ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <Tooltip title="Foreground color">
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setActiveColorSlot('fg')}
                sx={{
                  width: 27, height: 27, bgcolor: foregroundColor, border: 2,
                  borderColor: activeColorSlot === 'fg' ? 'primary.main' : 'common.white',
                  boxShadow: 2, cursor: 'pointer', zIndex: 1,
                }}
              />
            </Tooltip>
            <Tooltip title="Background color">
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setActiveColorSlot('bg')}
                sx={{
                  width: 27, height: 27, ml: -2, mt: 1, bgcolor: backgroundColor, border: 2,
                  borderColor: activeColorSlot === 'bg' ? 'primary.main' : 'common.white',
                  boxShadow: 2, cursor: 'pointer',
                }}
              />
            </Tooltip>
            <Tooltip title="Swap foreground and background colors">
              <IconButton size="small" onClick={() => {
                setForegroundColor(backgroundColor);
                setBackgroundColor(foregroundColor);
              }} aria-label="Swap foreground and background colors">
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box
              component="input"
              type="color"
              value={activeColorSlot === 'fg' ? foregroundColor : backgroundColor}
              onChange={(e) => activeColorSlot === 'fg' ? setForegroundColor(e.target.value) : setBackgroundColor(e.target.value)}
              aria-label={activeColorSlot === 'fg' ? 'Foreground color' : 'Background color'}
              sx={{ width: 36, height: 28, p: 0, border: 1, borderColor: 'divider', bgcolor: 'transparent', cursor: 'pointer' }}
            />
            <Typography variant="caption" color="text.secondary">
              {activeColorSlot === 'fg' ? 'Foreground' : 'Background'}
            </Typography>
          </Stack>
        ) : null}
              {floatObj ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 0 }}
          >
            <Typography variant="caption" color="text.secondary">
              Float active — drag body to move, corners to scale, orange handle
              to rotate. Enter commits · Esc discards.
            </Typography>
            <TextField
              size="small"
              label="Scale"
              type="number"
              value={Number(floatObj.scale.toFixed(2))}
              onChange={(e) => {
                const scale = clampFloatScale(Number(e.target.value));
                const next = withUniformFloatScale(floatObj, scale);
                floatObjRef.current = next;
                setFloatObj(next);
              }}
              inputProps={{ step: 0.05, min: 0.05, max: 8 }}
              sx={{ width: 100 }}
              disabled={busy}
            />
            <TextField
              size="small"
              label="Rotate °"
              type="number"
              value={Math.round(floatObj.rotationDeg)}
              onChange={(e) => {
                const rotationDeg = normalizeRotationDeg(Number(e.target.value));
                const next = { ...floatObj, rotationDeg };
                floatObjRef.current = next;
                setFloatObj(next);
              }}
              inputProps={{ step: 1 }}
              sx={{ width: 110 }}
              disabled={busy}
            />
            <Typography variant="caption" color="text.secondary">
              Clipboard: {clipboardReady ? 'ready' : 'empty'} (Ctrl+C / Ctrl+V)
            </Typography>
          </Stack>
        ) : null}
              <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 0 }}
        >
          <Tooltip title="Toggle grid overlay (not saved into PNG)">
            <ToggleButton
              size="small"
              value="grid"
              selected={showGrid}
              onChange={() => setShowGrid((v) => !v)}
              disabled={!imageReady || busy}
              aria-label="Toggle grid"
              sx={{ px: 0.75, minWidth: 36 }}
            >
              <GridOnIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={gridSize}
            onChange={(_e, next) => {
              if (next) setGridSize(next);
            }}
            disabled={!imageReady || busy || !showGrid}
          >
            {GRID_SIZE_OPTIONS.map((size) => (
              <ToggleButton
                key={size}
                value={size}
                sx={{ textTransform: 'none', px: 1, minWidth: 36 }}
              >
                {size}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Toggle rulers — click a ruler to add a guide">
            <ToggleButton
              size="small"
              value="rulers"
              selected={showRulers}
              onChange={() => setShowRulers((v) => !v)}
              disabled={!imageReady || busy}
              aria-label="Toggle rulers"
              sx={{ px: 0.75, minWidth: 36 }}
            >
              <StraightenIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip
            title={
              showTransparencyCheckerboard
                ? 'Transparency: checkerboard (display only)'
                : 'Transparency: solid gray (display only)'
            }
          >
            <ToggleButton
              size="small"
              value="transparency-bg"
              selected={showTransparencyCheckerboard}
              onChange={() => setShowTransparencyCheckerboard((v) => !v)}
              disabled={!imageReady || busy}
              aria-label="Toggle transparency background"
              data-testid="transparency-bg-toggle"
              sx={{ px: 0.75, minWidth: 36 }}
            >
              <TextureIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Snap paint strokes to grid and guides">
            <ToggleButton
              size="small"
              value="snap"
              selected={snapEnabled}
              onChange={() => setSnapEnabled((v) => !v)}
              disabled={!imageReady || busy}
              aria-label="Toggle snap"
              sx={{ px: 0.75, minWidth: 36 }}
            >
              <AutoFixHighOutlinedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Button
            size="small"
            onClick={() => {
              setVGuides([]);
              setHGuides([]);
            }}
            disabled={
              !imageReady || busy || (vGuides.length === 0 && hGuides.length === 0)
            }
            sx={{ textTransform: 'none' }}
          >
            Clear guides
          </Button>
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={() => adjustZoom(zoom - ZOOM_STEP)}
                disabled={!imageReady || busy || zoom <= ZOOM_MIN}
                aria-label="Zoom out"
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 40, textAlign: 'center' }}
          >
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={() => adjustZoom(zoom + ZOOM_STEP)}
                disabled={!imageReady || busy || zoom >= ZOOM_MAX}
                aria-label="Zoom in"
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Tooltip title="Undo (Ctrl+Z)">
              <span>
                <IconButton
                  size="small"
                  onClick={handleUndo}
                  disabled={!canUndo || busy}
                  aria-label="Undo"
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Shift+Z / Ctrl+Y)">
              <span>
                <IconButton
                  size="small"
                  onClick={handleRedo}
                  disabled={!canRedo || busy}
                  aria-label="Redo"
                >
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Flip horizontal">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleFlip('h')}
                  disabled={!imageReady || busy}
                  aria-label="Flip horizontal"
                >
                  <FlipOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Flip vertical">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleFlip('v')}
                  disabled={!imageReady || busy}
                  aria-label="Flip vertical"
                >
                  <FlipOutlinedIcon
                    fontSize="small"
                    sx={{ transform: 'rotate(90deg)' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Transform whole image (promote buffer into float)">
              <span>
                <IconButton
                  size="small"
                  onClick={promoteWholeCanvasToFloat}
                  disabled={!imageReady || busy}
                  aria-label="Transform whole image"
                >
                  <TransformOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              onClick={() => commitFloat()}
              disabled={!floatObj || busy}
              sx={{ textTransform: 'none' }}
            >
              Commit float
            </Button>
            <Button
              size="small"
              onClick={() => discardFloat()}
              disabled={!floatObj || busy}
              sx={{ textTransform: 'none' }}
            >
              Discard
            </Button>
          </Stack>
                </>
              ) : null}
            </Box>
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: panelLayersOpen ? 0.5 : 0, cursor: 'pointer' }}
                onClick={() => setPanelLayersOpen((v) => !v)}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Layers
                </Typography>
                <IconButton size="small" aria-label="Toggle Layers" sx={{ p: 0.25 }}>
                  {panelLayersOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Stack>
              {panelLayersOpen ? (
                <Stack spacing={0.75} sx={{ mb: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!imageReady || busy}
                    onClick={() => {
                      const doc = docRef.current;
                      if (!doc) return;
                      addEditorLayer(doc, { name: `Layer ${doc.layers.length + 1}` });
                      pushHistory();
                      presentDoc();
                    }}
                    sx={{ textTransform: 'none', fontSize: 11 }}
                  >
                    Add layer
                  </Button>
                  {[...(docRef.current?.layers || [])]
                    .slice()
                    .reverse()
                    .map((layer) => {
                      const active = docRef.current?.activeLayerId === layer.id;
                      const isDropTarget = layerDropTargetId === layer.id;
                      const isDragging = draggingLayerId === layer.id;
                      const canDelete =
                        (docRef.current?.layers?.length || 0) > 1;
                      const isRenaming = renamingLayerId === layer.id;
                      return (
                        <Box
                          key={layer.id}
                          onDragOver={(e) => {
                            if (!draggingLayerId || draggingLayerId === layer.id) {
                              return;
                            }
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setLayerDropTargetId(layer.id);
                          }}
                          onDragLeave={() => {
                            setLayerDropTargetId((cur) =>
                              cur === layer.id ? null : cur,
                            );
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const doc = docRef.current;
                            const fromId =
                              e.dataTransfer.getData(
                                'application/x-shifty-ie-layer',
                              ) || draggingLayerId;
                            setDraggingLayerId(null);
                            setLayerDropTargetId(null);
                            if (!doc || !fromId || fromId === layer.id) return;
                            const toIndex = doc.layers.findIndex(
                              (l) => l.id === layer.id,
                            );
                            if (toIndex < 0) return;
                            if (reorderEditorLayer(doc, fromId, toIndex)) {
                              pushHistory();
                              presentDoc();
                            }
                          }}
                          sx={{
                            border: 1,
                            borderColor: isDropTarget
                              ? 'primary.main'
                              : active
                                ? 'primary.main'
                                : 'divider',
                            outline: isDropTarget ? '2px solid' : undefined,
                            outlineColor: isDropTarget
                              ? 'primary.main'
                              : undefined,
                            borderRadius: 0.5,
                            px: 0.5,
                            py: 0.35,
                            bgcolor: isDropTarget
                              ? 'action.hover'
                              : active
                                ? 'action.selected'
                                : 'transparent',
                            opacity: isDragging ? 0.55 : 1,
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            const doc = docRef.current;
                            if (!doc || isRenaming) return;
                            setActiveEditorLayer(doc, layer.id);
                            bumpLayersUi();
                          }}
                          onDoubleClick={(e) => {
                            if (isRenaming || busy) return;
                            e.preventDefault();
                            e.stopPropagation();
                            if (isEditorTextLayer(layer)) {
                              beginTextLayerEdit(layer.id);
                              return;
                            }
                            beginRasterLayerTransform(layer.id);
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={0.25}>
                            <Tooltip title="Drag to reorder">
                              <Box
                                component="span"
                                draggable={!busy && !isRenaming}
                                onDragStart={(e) => {
                                  e.stopPropagation();
                                  e.dataTransfer.setData(
                                    'application/x-shifty-ie-layer',
                                    layer.id,
                                  );
                                  e.dataTransfer.effectAllowed = 'move';
                                  setDraggingLayerId(layer.id);
                                }}
                                onDragEnd={() => {
                                  setDraggingLayerId(null);
                                  setLayerDropTargetId(null);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: 'grab',
                                  color: 'text.secondary',
                                  touchAction: 'none',
                                }}
                                aria-label={`Drag to reorder ${layer.name}`}
                              >
                                <DragIndicatorIcon sx={{ fontSize: 16 }} />
                              </Box>
                            </Tooltip>
                            <Checkbox
                              size="small"
                              checked={layer.visible}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const doc = docRef.current;
                                if (!doc) return;
                                patchEditorLayer(doc, layer.id, {
                                  visible: e.target.checked,
                                });
                                presentDoc();
                                pushHistory();
                              }}
                              sx={{ p: 0.25 }}
                              inputProps={{ 'aria-label': `Toggle ${layer.name}` }}
                            />
                            {isEditorTextLayer(layer) ? (
                              <Box
                                component="span"
                                data-testid="layer-text-badge"
                                aria-label="Text layer"
                                title="Text layer (double-click row to edit)"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 16,
                                  height: 16,
                                  borderRadius: 0.5,
                                  border: 1,
                                  borderColor: 'divider',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                  color: 'text.secondary',
                                  flexShrink: 0,
                                  userSelect: 'none',
                                }}
                              >
                                T
                              </Box>
                            ) : null}
                            {isRenaming ? (
                              <TextField
                                size="small"
                                value={renamingLayerValue}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  renamingLayerValueRef.current = v;
                                  setRenamingLayerValue(v);
                                }}
                                onBlur={() => {
                                  const doc = docRef.current;
                                  const cancel = renameCancelRef.current;
                                  renameCancelRef.current = false;
                                  const layerId = layer.id;
                                  const originalName = layer.name;
                                  setRenamingLayerId(null);
                                  if (!doc) return;
                                  const decision = resolveLayerRenameCommit({
                                    cancel,
                                    nextName: renamingLayerValueRef.current,
                                    originalName,
                                  });
                                  if (!decision.commit) return;
                                  patchEditorLayer(doc, layerId, {
                                    name: decision.name,
                                  });
                                  pushHistory();
                                  bumpLayersUi();
                                }}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    renameCancelRef.current = false;
                                    e.currentTarget.blur();
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    renameCancelRef.current = true;
                                    renamingLayerValueRef.current = layer.name;
                                    setRenamingLayerValue(layer.name);
                                    setRenamingLayerId(null);
                                  }
                                }}
                                inputProps={{
                                  'aria-label': 'Rename layer',
                                  'data-testid': 'layer-rename-input',
                                  style: { fontSize: 11, padding: '2px 4px' },
                                }}
                                sx={{ flex: 1, minWidth: 0 }}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                noWrap
                                component="span"
                                sx={{
                                  flex: 1,
                                  fontWeight: active ? 700 : 500,
                                  minWidth: 0,
                                  userSelect: 'none',
                                }}
                                title={`${layer.name} (double-click to rename)`}
                                onDoubleClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  renameCancelRef.current = false;
                                  renamingLayerValueRef.current = layer.name;
                                  setRenamingLayerValue(layer.name);
                                  setRenamingLayerId(layer.id);
                                }}
                              >
                                {layer.name}
                              </Typography>
                            )}
                            <Tooltip title="Duplicate">
                              <IconButton
                                size="small"
                                aria-label={`Duplicate ${layer.name}`}
                                disabled={busy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const doc = docRef.current;
                                  if (!doc) return;
                                  if (duplicateEditorLayer(doc, layer.id)) {
                                    pushHistory();
                                    presentDoc();
                                  }
                                }}
                                sx={{ p: 0.2 }}
                              >
                                <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                canDelete
                                  ? 'Delete'
                                  : 'Cannot delete the last layer'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Delete ${layer.name}`}
                                  disabled={busy || !canDelete}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const doc = docRef.current;
                                    if (!doc) return;
                                    const result = deleteEditorLayer(
                                      doc,
                                      layer.id,
                                    );
                                    if (!result.ok) {
                                      setToolError(result.reason);
                                      return;
                                    }
                                    pushHistory();
                                    presentDoc();
                                  }}
                                  sx={{ p: 0.2 }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                          {active ? (
                            <Stack spacing={0.25} sx={{ pl: 0.5, pb: 0.25 }}>
                              <Typography variant="caption" color="text.secondary">
                                Opacity {Math.round(layer.opacity * 100)}%
                              </Typography>
                              <Slider
                                size="small"
                                min={0}
                                max={100}
                                value={Math.round(layer.opacity * 100)}
                                onChange={(_e, v) => {
                                  const doc = docRef.current;
                                  if (!doc) return;
                                  patchEditorLayer(doc, layer.id, {
                                    opacity: Number(v) / 100,
                                  });
                                  presentDoc();
                                  bumpLayersUi();
                                }}
                                onChangeCommitted={() => pushHistory()}
                              />
                              <FormControl size="small" fullWidth>
                                <InputLabel id={`blend-${layer.id}`}>Blend</InputLabel>
                                <Select
                                  labelId={`blend-${layer.id}`}
                                  label="Blend"
                                  value={layer.blendMode || BLEND_NORMAL}
                                  onChange={(e) => {
                                    const doc = docRef.current;
                                    if (!doc) return;
                                    patchEditorLayer(doc, layer.id, {
                                      blendMode: String(e.target.value),
                                    });
                                    presentDoc();
                                    pushHistory();
                                  }}
                                  sx={{ fontSize: 11 }}
                                >
                                  {EDITOR_BLEND_MODES.map((m) => (
                                    <MenuItem key={m} value={m}>
                                      {m === BLEND_NORMAL
                                        ? 'Normal'
                                        : m === BLEND_MULTIPLY
                                          ? 'Multiply'
                                          : m === BLEND_SCREEN
                                            ? 'Screen'
                                            : 'Overlay'}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          ) : null}
                        </Box>
                      );
                    })}
                </Stack>
              ) : null}
            </Box>
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: panelToolOpen ? 0.5 : 0, cursor: 'pointer' }}
                onClick={() => setPanelToolOpen((v) => !v)}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Tool options
                </Typography>
                <IconButton size="small" aria-label="Toggle Tool options" sx={{ p: 0.25 }}>
                  {panelToolOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Stack>
              {panelToolOpen ? (
              <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Primary tool options appear in the bar above the canvas.
              </Typography>
              {tool === TOOL_REMOVE_BG ? (
          <Box sx={{ mt: 0, mb: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 0.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: sampledColor
                    ? `rgba(${sampledColor.r}, ${sampledColor.g}, ${sampledColor.b}, 1)`
                    : 'transparent',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 130 }}
              >
                {sampledColor
                  ? `RGB(${sampledColor.r}, ${sampledColor.g}, ${sampledColor.b})`
                  : 'No color sampled yet'}
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={handleApplyRemoveBg}
              disabled={!imageReady || !sampledColor || busy}
              startIcon={<CheckIcon fontSize="small" />}
              sx={{ textTransform: 'none', mt: 0.5 }}
            >
              Apply remove background
            </Button>
          </Box>
        ) : tool === TOOL_PAINT ? (
          <Box sx={{ mt: 0, mb: 0 }}>
            <Tooltip title="Sample color from image">
              <ToggleButton
                size="small"
                value="eyedropper"
                selected={eyeDropper}
                onChange={() => setEyeDropper((v) => !v)}
                disabled={!imageReady || busy}
                aria-label="Eye-dropper"
                sx={{ px: 1 }}
              >
                <ColorizeOutlinedIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </Box>
        ) : isSelectionBuildingTool(tool) ? (
          <Stack direction="column" spacing={0.75} alignItems="stretch" sx={{ mt: 0, mb: 0 }}>
            {selectMode === SELECT_MAGNETIC ? (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Edge snap: {magneticRadius}px
                </Typography>
                <Slider
                  size="small"
                  min={4}
                  max={24}
                  value={magneticRadius}
                  onChange={(_e, value) =>
                    setMagneticRadius(Array.isArray(value) ? value[0] : value)
                  }
                  disabled={!imageReady || busy}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  v1: local Sobel snap (not full livewire)
                </Typography>
              </Box>
            ) : null}
            {selectMode === SELECT_POLY ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Click corners · click near start (cyan ring), double-click, or
                  Enter to close · Esc cancels
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => closePolygonalSelection()}
                  disabled={
                    !imageReady ||
                    busy ||
                    !(
                      selectDraft?.type === 'poly' &&
                      Array.isArray(selectDraft.points) &&
                      selectDraft.points.length >= 3
                    )
                  }
                  sx={{ textTransform: 'none' }}
                >
                  Close polygon
                </Button>
              </>
            ) : null}
            {tool === TOOL_SELECT ? (
              <>
                <Button
                  size="small"
                  onClick={liftSelection}
                  disabled={!selectionHasPixels(selection) || busy}
                  startIcon={<ContentCutOutlinedIcon fontSize="small" />}
                  sx={{ textTransform: 'none' }}
                >
                  Lift / Cut
                </Button>
                <Button
                  size="small"
                  onClick={clearSelection}
                  disabled={!selectionHasPixels(selection) || busy}
                  sx={{ textTransform: 'none' }}
                >
                  Deselect
                </Button>
              </>
            ) : null}
            {tool === TOOL_INPAINT ? (
              <Stack spacing={0.75}>
                {!selectionHasPixels(selection) ? (
                  <Alert severity="info" sx={{ py: 0, px: 1 }}>
                    Make a selection first
                  </Alert>
                ) : null}
                <TextField
                  size="small"
                  label="Inpaint prompt"
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  multiline
                  minRows={2}
                  disabled={!imageReady || busy || generateBusy}
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={generateSoftenMask}
                      onChange={(e) => setGenerateSoftenMask(e.target.checked)}
                      disabled={!imageReady || busy || generateBusy}
                    />
                  }
                  label={<Typography variant="caption">Soften mask edge</Typography>}
                />
                {generateError ? (
                  <Alert severity="error" sx={{ py: 0 }} onClose={() => setGenerateError('')}>
                    {generateError}
                  </Alert>
                ) : null}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    generateBusy ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <AutoAwesomeOutlinedIcon fontSize="small" />
                    )
                  }
                  disabled={
                    !imageReady ||
                    busy ||
                    generateBusy ||
                    !selectionHasPixels(selection) ||
                    !(generatePrompt || '').trim()
                  }
                  onClick={() => runGenerate(GENERATE_MODE_INPAINT)}
                  sx={{ textTransform: 'none' }}
                >
                  Run inpaint
                </Button>
                <Button
                  size="small"
                  onClick={clearSelection}
                  disabled={!selectionHasPixels(selection) || busy}
                  sx={{ textTransform: 'none' }}
                >
                  Deselect
                </Button>
              </Stack>
            ) : null}
          </Stack>
        ) : tool === TOOL_CROP ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0, mb: 0 }}>
            <Button variant="outlined" size="small" onClick={applyCrop} disabled={!cropDraft || Math.abs(cropDraft.x1 - cropDraft.x0) < 1 || Math.abs(cropDraft.y1 - cropDraft.y0) < 1 || busy} startIcon={<CropOutlinedIcon fontSize="small" />} sx={{ textTransform: 'none' }}>
              Apply crop
            </Button>
            <Button size="small" onClick={() => setCropDraft(null)} disabled={!cropDraft || busy} sx={{ textTransform: 'none' }}>Cancel crop</Button>
          </Stack>
        ) : tool === TOOL_SAMPLER ? (
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0, mb: 0 }}>
            {colorSamples.length ? colorSamples.map((sample, index) => (
              <Button key={`${sample.x}-${sample.y}-${index}`} size="small" onClick={() => { setForegroundColor(rgbToHex(sample)); setActiveColorSlot('fg'); }} sx={{ textTransform: 'none', minWidth: 0, borderColor: 'divider' }} variant="outlined">
                <Box sx={{ width: 12, height: 12, mr: 0.75, bgcolor: `rgb(${sample.r}, ${sample.g}, ${sample.b})`, border: 1, borderColor: 'divider' }} />
                RGB({sample.r}, {sample.g}, {sample.b})
              </Button>
            )) : <Typography variant="caption" color="text.secondary">Click the image to track up to four color samples.</Typography>}
            <Button size="small" onClick={() => setColorSamples([])} disabled={!colorSamples.length || busy} sx={{ textTransform: 'none' }}>Clear samples</Button>
          </Stack>
        ) : tool === TOOL_GRADIENT ? (
          <Box sx={{ mt: 0, mb: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Drag on the canvas for a linear FG→BG gradient
              {selectionHasPixels(selection)
                ? ' (limited to the active selection).'
                : ' (full canvas — Fill also ignores selection today).'}
            </Typography>
          </Box>
        ) : null}
              </>
              ) : null}
            </Box>
          </Box>
        </Box>
      </DialogContent>
        </>
      )}

      <ImageEditorNewCanvasDialog
        open={newDialogOpen}
        width={newWidth}
        height={newHeight}
        filename={newFilename}
        onWidthChange={setNewWidth}
        onHeightChange={setNewHeight}
        onFilenameChange={setNewFilename}
        onCancel={() => setNewDialogOpen(false)}
        onCreate={handleNewDocument}
        busy={busy}
      />

      <Dialog
        open={generateDialogOpen}
        onClose={
          busy || generateBusy ? undefined : () => setGenerateDialogOpen(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 1 }}>Generate</DialogTitle>
        <DialogContent>
          {generateError ? (
            <Alert
              severity="error"
              sx={{ mb: 1, py: 0 }}
              onClose={() => setGenerateError('')}
            >
              {generateError}
            </Alert>
          ) : null}
          <ToggleButtonGroup
            exclusive
            size="small"
            fullWidth
            value={generateMode}
            disabled={generateBusy}
            onChange={(_e, next) => {
              if (next) {
                setGenerateMode(next);
                setGenerateError('');
              }
            }}
            sx={{ mb: 1.5, mt: 0.5 }}
          >
            <ToggleButton value={GENERATE_MODE_TXT2IMG}>
              Text to image
            </ToggleButton>
            <ToggleButton
              value={GENERATE_MODE_IMG2IMG}
              disabled={!imageReady}
            >
              Image to image
            </ToggleButton>
            <ToggleButton
              value={GENERATE_MODE_INPAINT}
              disabled={!imageReady}
            >
              Inpaint
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Prompt"
            value={generatePrompt}
            disabled={generateBusy}
            onChange={(e) => setGeneratePrompt(e.target.value)}
            placeholder={
              generateMode === GENERATE_MODE_IMG2IMG
                ? 'Describe how to change the current image…'
                : generateMode === GENERATE_MODE_INPAINT
                  ? 'Describe what should appear in the selection…'
                  : 'Describe the image to generate…'
            }
          />
          {generateMode === GENERATE_MODE_IMG2IMG ? (
            <Box sx={{ mt: 1.5, px: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Strength ({generateStrength.toFixed(2)}) — higher changes more.
                Uses the active float when present, otherwise the canvas.
              </Typography>
              <Slider
                size="small"
                min={0.05}
                max={0.95}
                step={0.05}
                value={generateStrength}
                disabled={generateBusy}
                onChange={(_e, v) => setGenerateStrength(Number(v))}
                valueLabelDisplay="auto"
              />
            </Box>
          ) : null}
          {generateMode === GENERATE_MODE_INPAINT ? (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {!selectionHasPixels(selection) ? (
                <Alert severity="info" sx={{ py: 0 }}>
                  Make a selection first
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Inpaint fills the current selection (Stability mask: white =
                  edit). Result lands as a full-frame float.
                </Typography>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={generateSoftenMask}
                    onChange={(e) => setGenerateSoftenMask(e.target.checked)}
                    disabled={generateBusy}
                  />
                }
                label="Soften mask edge (expand ~2px)"
              />
              <Typography variant="caption" color="text.secondary">
                Soften is off by default so the selection boundary stays exact.
              </Typography>
            </Stack>
          ) : null}
          {!companyId ? (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              Workspace company is required before generating.
            </Typography>
          ) : null}
          {projectId ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Project spend cap applies when set on this Game Studio project.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              No project selected — workspace monthly AI budget still applies.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setGenerateDialogOpen(false)}
            disabled={generateBusy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={runGenerate}
            disabled={
              generateBusy ||
              busy ||
              !companyId ||
              !(generatePrompt || '').trim() ||
              (generateMode === GENERATE_MODE_IMG2IMG && !imageReady) ||
              (generateMode === GENERATE_MODE_INPAINT &&
                (!imageReady || !selectionHasPixels(selection)))
            }
            startIcon={
              generateBusy ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AutoAwesomeOutlinedIcon fontSize="small" />
              )
            }
            sx={{ textTransform: 'none' }}
          >
            {generateBusy ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageSizeDialogOpen} onClose={busy ? undefined : () => setImageSizeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1 }}>Image Size</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              size="small"
              label="Width"
              type="number"
              value={imageWidth}
              onChange={(e) => updateImageDimension('width', e.target.value)}
              inputProps={{ min: 1, max: MAX_CANVAS_DIMENSION }}
              fullWidth
            />
            <TextField
              size="small"
              label="Height"
              type="number"
              value={imageHeight}
              onChange={(e) => updateImageDimension('height', e.target.value)}
              inputProps={{ min: 1, max: MAX_CANVAS_DIMENSION }}
              fullWidth
            />
          </Stack>
          <FormControlLabel
            control={<Checkbox size="small" checked={lockImageAspect} onChange={(e) => setLockImageAspect(e.target.checked)} />}
            label="Lock aspect ratio"
            sx={{ mt: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Dimensions are clamped to 1–{MAX_CANVAS_DIMENSION}px. Resampling is smooth.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageSizeDialogOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={applyImageSize} disabled={busy}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={canvasSizeDialogOpen} onClose={busy ? undefined : () => setCanvasSizeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1 }}>Canvas Size</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              size="small"
              label="Width"
              type="number"
              value={newCanvasWidth}
              onChange={(e) => setNewCanvasWidth(e.target.value)}
              inputProps={{ min: 1, max: MAX_CANVAS_DIMENSION }}
              fullWidth
            />
            <TextField
              size="small"
              label="Height"
              type="number"
              value={newCanvasHeight}
              onChange={(e) => setNewCanvasHeight(e.target.value)}
              inputProps={{ min: 1, max: MAX_CANVAS_DIMENSION }}
              fullWidth
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
            Anchor existing pixels
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={canvasAnchor}
            onChange={(_event, next) => next && setCanvasAnchor(next)}
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}
          >
            {[
              ['nw', '↖'], ['n', '↑'], ['ne', '↗'],
              ['w', '←'], ['center', '•'], ['e', '→'],
              ['sw', '↙'], ['s', '↓'], ['se', '↘'],
            ].map(([value, label]) => (
              <ToggleButton key={value} value={value} aria-label={`Anchor ${value}`} sx={{ minWidth: 44 }}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCanvasSizeDialogOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={applyCanvasSize} disabled={busy}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rasterizeTextDialogOpen}
        onClose={() => {
          rasterizeTextLayerIdRef.current = null;
          setRasterizeTextDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        data-testid="rasterize-text-layer-dialog"
      >
        <DialogTitle sx={{ py: 1 }}>Rasterize this text layer first?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Painting requires a raster layer. Rasterizing converts this text to
            pixels and removes live text editing for this layer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              rasterizeTextLayerIdRef.current = null;
              setRasterizeTextDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => confirmRasterizeTextLayer()}
            data-testid="rasterize-text-layer-confirm"
          >
            Rasterize
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveDialogOpen}
        onClose={busy ? undefined : () => setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5 }}>Save as new asset variant</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Choose Image (flattened export) or Project (layered reopen).
            Zoom/pan/guides are never baked in.
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={saveKind}
            onChange={(_e, next) => {
              if (next) setSaveKind(next);
            }}
            disabled={busy}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="image" sx={{ textTransform: 'none' }}>
              Image
            </ToggleButton>
            <ToggleButton value="project" sx={{ textTransform: 'none' }}>
              Project (layers)
            </ToggleButton>
          </ToggleButtonGroup>
          {saveKind === 'image' ? (
          <FormControl size="small" fullWidth sx={{ mb: 1.5 }} disabled={busy}>
            <InputLabel id="save-format-label">Format</InputLabel>
            <Select
              labelId="save-format-label"
              label="Format"
              value={saveFormat}
              onChange={(e) => {
                setSaveFormat(normalizeImageEditorFormat(e.target.value));
                setJpegAlphaAck(false);
              }}
            >
              {IMAGE_EDITOR_FORMATS.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.label} — {f.hint}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          ) : (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Layered project saves preserve all layers for reopening and continue
              editing.
            </Alert>
          )}
          <TextField
            size="small"
            label="Filename"
            value={saveFilename}
            onChange={(e) => setSaveFilename(e.target.value)}
            placeholder="e.g. hero_banner"
            disabled={busy}
            autoFocus
            fullWidth
            helperText="Whole name — no forced prefix."
            inputProps={{ 'aria-label': 'Filename' }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.25, fontFamily: 'monospace' }}
          >
            {savePreview.valid
              ? `Save as: ${savePreview.fileName}`
              : 'Enter a filename'}
          </Typography>
          {saveKind === 'image' && saveFormat === 'jpeg' && saveHasAlpha ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Transparency will be lost / flattened to {backgroundColor}.
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={jpegAlphaAck}
                    onChange={(e) => setJpegAlphaAck(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="I understand — flatten and save as JPEG"
                sx={{ display: 'flex', mt: 0.5, ml: 0 }}
              />
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => setSaveDialogOpen(false)}
            disabled={busy}
            sx={{ textTransform: 'none' }}
          >
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            onClick={confirmDownload}
            disabled={
              busy ||
              saveKind !== 'image' ||
              !savePreview.valid ||
              (saveFormat === 'jpeg' && saveHasAlpha && !jpegAlphaAck)
            }
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
          <Tooltip
            title={
              saveKind === 'project' && typeof onSaveProject !== 'function'
                ? 'Layered project saving is unavailable in this context.'
                : saveKind === 'image' && jpegNeedsAlphaWarning
                  ? 'Confirm the JPEG transparency warning first.'
                  : ''
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={confirmSave}
                disabled={
                  busy ||
                  saveInFlight ||
                  !savePreview.valid ||
                  (saveKind === 'image' &&
                    (typeof onSave !== 'function' || jpegNeedsAlphaWarning)) ||
                  (saveKind === 'project' &&
                    typeof onSaveProject !== 'function')
                }
                startIcon={
                  busy || saveInFlight ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : null
                }
                sx={{ textTransform: 'none' }}
                data-testid="image-editor-save-confirm"
              >
                {saveInFlight
                  ? 'Saving…'
                  : saveKind === 'project'
                    ? 'Save Project'
                    : 'Save Image'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
